import React, { Component } from "react";
import {
  Row,
  Col,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  Card,
  CardBody,
} from "reactstrap";
import axios from "axios";
import { END_POINT, END_POINT_SOCKET } from "../../../constants/defaultValues";
import {
  SetItemsSelected,
  NotificatioErrController,
  NotificatioSucController,
  showApiError,
  CleanPrice,
  RandomString,
  getCurrentDate,
} from "../../../helpers/Utils";
import createSocket from "../../../helpers/createSocket";
import {
  PageHeading,
  ListItemLeftForm,
  ListItemFormMobile,
  ProductApplicationMenu,
  ContentDisplay,
  ContentDisplayCost,
  Category,
  ProductModernDashboard,
  AddFromTemplateModal,
} from "../../../containers/pages/product";
import { Pagination } from "../../../containers/pages/general";
import LogsHelper from "../../../helpers/Logs";
import IntlMessages from "../../../helpers/IntlMessages";
import classnames from "classnames";
import { isC, isR, isU, isD } from "../../../helpers/Roles";
import { NavLink } from "react-router-dom";
import { Colxx, Separator } from "../../../components/common/CustomBootstrap";
import TemplateWarningCard from "../../../components/common/TemplateWarningCard";
import TooltipItem from "../../../components/common/TooltipItem";
import FooterPage from "../../../containers/pages/FooterPage";
import AddProduct from "../../../containers/pages/product/AddProduct";
import AddCategoryProduct from "../../../containers/pages/product/AddCategoryProduct";
import ContentMultiForm from "../../../containers/pages/product/ContentMultiForm";
import FooterProduct from "../../../containers/pages/product/FooterProduct";
import { connect } from "react-redux";
import {
  clickOnMobileMenu,
  setContainerClassnames,
  setSearchContext,
} from "../../../redux/actions";
import isEqual from "lodash.isequal";
import moment from "moment";
import { injectIntl } from "react-intl";
import * as productApi from "./product/productApi";
import * as variantHandlers from "./product/productVariantHandlers";
import {
  validateAndBuildStock,
  validateAndBuildNewStock,
  buildCostDataFromResponse,
  buildStockEntriesFromProduct,
} from "./product/productCostHandlers";
import {
  FNB_TYPE_DRINK,
  ensureDrinkOptionValues,
  getDefaultFnbType,
  getFnbFormDefaults,
  normalizeFnbPayloadOptions,
} from "./product/productFnbType";

const DEFAULT_SIZE_NAMES = ["Size S", "Size M", "Size L"];

const createDefaultSizes = () =>
  DEFAULT_SIZE_NAMES.map((name, index) => ({
    id: `default-size-${index + 1}`,
    name,
    value: "0",
    promo_price: "0",
  }));

const createDefaultToppings = () =>
  Array.from({ length: 2 }, (_, index) => ({
    id: `default-topping-${index + 1}`,
    name: "",
    value: "0",
    promo_price: "0",
  }));

const createDefaultFlavors = () => [
  {
    id: "default-flavor-1",
    name: "",
    value: "0",
    promo_price: "0",
  },
];

const ensureDefaultFlavors = (flavors = []) => {
  const normalizedFlavors = Array.isArray(flavors) ? [...flavors] : [];
  return normalizedFlavors.length ? normalizedFlavors : createDefaultFlavors();
};

const ensureDefaultToppings = (toppings = []) => {
  const normalizedToppings = Array.isArray(toppings) ? [...toppings] : [];

  while (normalizedToppings.length < 2) {
    normalizedToppings.push({
      id: `default-topping-${normalizedToppings.length + 1}`,
      name: "",
      value: "0",
      promo_price: "0",
    });
  }

  return normalizedToppings;
};

const ensureDefaultSizes = (sizes = []) => {
  const normalizedSizes = Array.isArray(sizes) ? [...sizes] : [];

  DEFAULT_SIZE_NAMES.forEach((name, index) => {
    if (
      !normalizedSizes.some(
        (size) =>
          String(size?.name || "").toLowerCase() === name.toLowerCase(),
      )
    ) {
      normalizedSizes.push({
        id: `default-size-${index + 1}`,
        name,
        value: "0",
        promo_price: "0",
      });
    }
  });

  return normalizedSizes;
};

const getPersistedSizes = (sizes = [], businessType = "") => {
  const isFoodService =
    businessType === "cafe" || businessType === "restaurant";
  if (!Array.isArray(sizes) || !isFoodService) return sizes || [];

  return sizes.filter((size) => {
    const normalizedName = String(size?.name || "").toLowerCase();
    const isSampleSize =
      String(size?.id || "").startsWith("default-size-") ||
      DEFAULT_SIZE_NAMES.some(
        (defaultName) => defaultName.toLowerCase() === normalizedName,
      );

    if (!isSampleSize) return true;
    return (Number.parseFloat(CleanPrice(size?.value)) || 0) > 0;
  });
};

const getPersistedToppings = (toppings = []) => {
  if (!Array.isArray(toppings)) return [];

  return toppings
    .filter((topping) => String(topping?.name || "").trim() !== "")
    .map((topping) => ({
      ...topping,
      name: String(topping.name).trim(),
      value: CleanPrice(topping?.value || "0") || "0",
    }));
};

const getPersistedFlavors = (flavors = []) => getPersistedToppings(flavors);

const parseStoredProductImages = (product = {}) => {
  const images = product.images;

  if (images) {
    if (Array.isArray(images)) return images.filter(Boolean);

    try {
      const parsedImages = JSON.parse(images);
      if (Array.isArray(parsedImages)) return parsedImages.filter(Boolean);
    } catch (_err) {
      const imageList = String(images)
        .split(",")
        .map((image) => image.trim())
        .filter(Boolean);

      if (imageList.length) return imageList;
    }
  }

  return product.image ? [product.image] : [];
};

const hasSizeBasedPrice = (sizes = [], businessType = "") => {
  const isFoodService =
    businessType === "cafe" || businessType === "restaurant";
  if (!Array.isArray(sizes) || !isFoodService) return false;

  return sizes.some(
    (size) => (Number.parseFloat(CleanPrice(size?.value || "")) || 0) > 0,
  );
};

const getSubmittedProductPrice = (price, sizes = [], businessType = "") =>
  hasSizeBasedPrice(sizes, businessType) ? "0" : CleanPrice(price);

const isFoodServiceBusiness = (businessType = "") =>
  businessType === "cafe" || businessType === "restaurant";

const isMissingPrice = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const getOptionalInventoryValue = (value) =>
  isMissingPrice(value) ? "" : CleanPrice(value);

const decodeStoredProductText = (value = "") => {
  const entities = { amp: "&", lt: "<", gt: ">", quot: '"', "#x27": "'", "#39": "'" };
  let text = String(value ?? "");
  for (let pass = 0; pass < 8; pass++) {
    const decoded = text.replace(
      /&(amp|lt|gt|quot|#x27|#39);/g,
      (_match, entity) => entities[entity],
    );
    if (decoded === text) break;
    text = decoded;
  }
  return text;
};

const isPositivePriceValue = (value) =>
  (Number.parseFloat(CleanPrice(value)) || 0) > 0;

const getStockQuantityValue = (stock = {}, product = {}) =>
  stock?.count ?? product?.stock_quantity ?? product?.quantitative ?? "";

const getStockCostPriceValue = (stock = {}, product = {}) =>
  stock?.avarage_price ??
  stock?.average_price ??
  stock?.price_new ??
  stock?.price ??
  product?.stock_cost_price ??
  product?.price_cost ??
  "";

const getDirectProductStock = (productId, makeProducts = [], stocks = []) => {
  const linkedMake = makeProducts.find(
    (item) =>
      String(item.product_id) === String(productId) &&
      (item.stock_id || item.stock?.id),
  );
  if (!linkedMake) return null;
  return (
    linkedMake.stock ||
    stocks.find(
      (stock) => String(stock.id) === String(linkedMake.stock_id || linkedMake.stock?.id),
    ) ||
    null
  );
};

const DEFAULT_FORM = {
  id: "",
  name: "",
  sku: "",
  barcode: "",
  brand_name: "",
  sale_unit: "",
  softHide: false,
  tax_rate: 0,
  weight: "",
  length: "",
  width: "",
  height: "",
  price: "",
  price_sale: "",
  price_wholesale: "",
  date_start: null,
  date_end: null,
  active_sale: false,
  category: null,
  category_id: 0,
  price_cost: "",
  quantity: "",
  quantitative: "",
  image: "",
  images: [], // New: Store multiple files
  images_old: [], // New: Store existing image URLs (for update)
  size: [],
  color: [],
  material: [],
  // size: [
  //     { "label": "Size S", "value": "Size S" },
  //     { "label": "Size M", "value": "Size M" },
  //     { "label": "Size L", "value": "Size L" },
  // ],
  // color: [
  //     { "label": "Màu trắng", "value": "Màu trắng" },
  //     { "label": "Màu đen", "value": "Màu đen" },
  // ],
  topping: [],
  flavor: [],
  fnb_type: null,
  ice: ["0", "25", "50", "75", "100"],
  sugar: ["0", "25", "50", "75", "100"],
  sizeTitle: "Size",
  toppingTitle: "Topping",
  iceTitle: "Lượng đá",
  sugarTitle: "Lượng đường",
  ingredients: [], // Định lượng nguyên liệu cho Recipe Builder
  isTemplate: false,
  stock_quantity: "", // Số lượng nhập kho cho Fashion/Retail/Grocery
  stock_cost_price: "", // Giá vốn cho Fashion/Retail/Grocery
  dynamic_form: [],
};

const createDefaultProductForm = (businessType = "") => ({
  ...DEFAULT_FORM,
  ...getFnbFormDefaults(businessType),
  size: isFoodServiceBusiness(businessType) ? createDefaultSizes() : [],
  topping: isFoodServiceBusiness(businessType) ? createDefaultToppings() : [],
  flavor: isFoodServiceBusiness(businessType) ? createDefaultFlavors() : [],
  images: [],
  images_old: [],
});

const DEFAULT_FORM_COST = {
  id: "",
  count: 0,
  stock: null,
  product: null,
  make: [],
  make_new: [],
  data_products: [],
  data_stocks: [],

  new_product_id: "",
};

const PAGE_SIZE_MULTI = 15; // Số sản phẩm trên mỗi trang

const ROLE_ALIAS = "product";
const USE_MODERN_PRODUCT_UI = true;

class ContentComponent extends Component {
  constructor(props) {
    super(props);
    const { messages } = props.intl;

    this.state = {
      business_type: "",
      inputValue: "",
      page: {
        pageSizes: [12, 24],
        selectedPageSize: 12,
        currentPage: 1,
        totalPage: 1,
        totalItem: 0,
        search: "",
      },
      pageMulti: {
        currentPage: 1,
        totalItems: 0,
      },
      selectedItems: [],
      selectedCategoryItems: [],
      data: [],
      data_all: [],
      changedProducts: {},
      license: {},
      data_length: 0,
      hide_button_add: false,
      productLimit: Infinity,
      totalProduct: 0,

      data_product: null,
      filter: null,

      data_dynamic_form: [],
      data_categories: [],
      data_stocks: [],
      make_products: [],
      is_loading_data: true,
      data_providers: [],
      show: {
        add: false,
        update: false,
        update_cost: false,
        view: false,
        viewonly: false,
      },
      accordion: [],
      activeTab: "1",
      showAddCategory: false,
      showAddFromTemplate: false,
      hasTemplates: false,
      showTemplate: false,
      displayMode: "thumblist",
      form: createDefaultProductForm(),
      form_cost: DEFAULT_FORM_COST,
      categoriesTooltip: {
        placement: "top",
        text: "",
        body: messages["product.tooltip.categories"],
      },
      show_add_more_topping: false,
      show_quantitative: false,
      sku: "",
      sku_stock: "",
      editProduct: false,
      isSavingProduct: false,
      successfulImports: [],
      failedImports: [],
      lastChecked: null,
      generatedVariants: [],
      masterVariantSettings: {
        price: "",
        price_wholesale: "",
        promo_price: "",
        quantity: "",
        cost_price: "",
        exchange_value: "",
        date_start: null,
        date_end: null,
      },
      allSizeOptions: [
        // { "label": "Size S", "value": "Size S" },
        // { "label": "Size M", "value": "Size M" },
        // { "label": "Size L", "value": "Size L" },
      ],
      allColorOptions: [
        // { "label": "Màu trắng", "value": "Màu trắng" },
        // { "label": "Màu đen", "value": "Màu đen" },
      ],
      allMaterialOptions: [
        // { label: 'Cotton', value: 'cotton' },
        // { label: 'Kaki', value: 'kaki' },
        // { label: 'Vải mè', value: 'me' },
      ],
      // isLoadingSubmit: false,
    };

    this.searchTimeoutId = null;
    this.socket = createSocket();
  }

  handleRefreshProductList = () => {
    this.dataListRender();
    this.dataAllRender();
    this.fetchUserTemplateVisibility();
  };

  fetchUserTemplateVisibility = () => {
    let tokenStr = localStorage.getItem("access_token");
    if (!tokenStr) return;
    axios
      .get(`${END_POINT}/user`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      })
      .then((res) => res.data)
      .then((data) => {
        if (data && data.data) {
          this.setState({
            showTemplate: Boolean(
              data.data.showTemplate ?? data.data.show_template,
            ),
          });
        }
      })
      .catch(() => {});
  };

  componentDidMount() {
    this.socket.emit("join room", {
      room_id: localStorage.getItem("user_id"),
    });

    const parentId = localStorage.getItem("b");
    if (parentId) {
      this.socket.emit("join room", {
        room_id: parentId,
      });
    }

    // Set search context cho Navbar
    this.props.setSearchContext("product");

    this.socket.on("refresh-product", this.handleRefreshProductList);
    this.socket.on("refresh-template-data", (data) => {
      if (data && typeof data.show_template !== "undefined") {
        this.setState({
          showTemplate: Boolean(data.show_template),
        });
      } else {
        this.fetchUserTemplateVisibility();
      }
    });

    this.dataListRender();
    this.dataAllRender();
    this.dataAllRenderSoftDel();
    this.dataAllRenderStockSoftDel();
    this.getLicense();
    this.getBusinessType();
    this.dataRenderProviders();
    this.renderSKU();
    this.fetchUserTemplateVisibility();

    window.addEventListener("refresh-product-list", this.handleRefreshProductList);

    // Kiểm tra URL param để tự động mở form thêm sản phẩm
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("action") === "add") {
      this.setState((prevState) => ({
        show: { ...prevState.show, add: true },
      }));
      // Xóa param khỏi URL để tránh mở lại khi refresh
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("refresh-product-list", this.handleRefreshProductList);
    if (this.socket) {
      this.socket.off("refresh-product", this.handleRefreshProductList);
      this.socket.off("refresh-template-data");
      this.socket.close();
    }
    // Rất quan trọng: Dọn dẹp timeout khi component bị unmount
    // để tránh memory leak hoặc lỗi gọi hàm trên component đã unmount.
    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId);
    }
  }

  componentDidUpdate(prevProps) {
    // Listen thay đổi searchKeyword từ Redux (Navbar search)
    if (prevProps.searchKeyword !== this.props.searchKeyword) {
      // Debounce để tránh search quá nhiều
      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
      }
      this.searchTimeoutId = setTimeout(() => {
        this.performSearch(this.props.searchKeyword);
      }, 300);
    }
  }

  checkTemplatesExist = async (businessType) => {
    let industryType = "TAP_HOA";
    if (businessType) {
      const bt = businessType.trim().toLowerCase();
      if (bt === "grocery") {
        industryType = "TAP_HOA";
      } else if (bt === "spa" || bt === "salon") {
        industryType = "spa";
      } else if (bt === "pharmacy") {
        industryType = "pharmacy";
      } else if (bt === "electronics") {
        industryType = "electronic";
      } else if (["cafe", "restaurant", "retail", "fashion", "cosmetic"].includes(bt)) {
        industryType = bt;
      } else if (bt === "cosmetics") {
        industryType = "cosmetic";
      }
    }
    try {
      const response = await axios.get(`${END_POINT}/product-template`, {
        params: {
          industry_type: industryType,
          search: "",
        },
      });
      if (response.data && response.data.data && response.data.data.length > 0) {
        this.setState({ hasTemplates: true });
      } else {
        this.setState({ hasTemplates: false });
      }
    } catch (error) {
      console.error("Error checking templates:", error);
      this.setState({ hasTemplates: false });
    }
  };

  getBusinessType = async () => {
    try {
      const businessType = await productApi.fetchBusinessType();
      this.setState((prev) => {
        const defaults = createDefaultProductForm(businessType);
        if (prev.editProduct || prev.form.id) {
          const inferredFnbType = prev.form.fnb_type || defaults.fnb_type;
          return {
            business_type: businessType,
            form:
              inferredFnbType === prev.form.fnb_type
                ? prev.form
                : { ...prev.form, fnb_type: inferredFnbType },
          };
        }

        if (prev.form.fnb_type) {
          return { business_type: businessType };
        }

        return {
          business_type: businessType,
          form: {
            ...prev.form,
            fnb_type: defaults.fnb_type,
            ice: ensureDrinkOptionValues(prev.form.ice),
            sugar: ensureDrinkOptionValues(prev.form.sugar),
            size:
              Array.isArray(prev.form.size) && prev.form.size.length
                ? prev.form.size
                : defaults.size,
            topping: Array.isArray(prev.form.topping) && prev.form.topping.length
              ? prev.form.topping
              : defaults.topping,
            flavor: Array.isArray(prev.form.flavor) && prev.form.flavor.length
              ? prev.form.flavor
              : defaults.flavor,
          },
        };
      });
      this.checkTemplatesExist(businessType);
    } catch (err) {
      showApiError(err, this.props.intl);
    }
  };

  getLicense = () => {
    productApi.fetchLicense()
      .then((userLicense) => {
        let currentDate = getCurrentDate();
        if (userLicense.expired_date < currentDate && !userLicense.upgraded) {
          this.setState({ license: { using: false } });
        }
        this.hideButtonAdd();
      })
      .catch((err) => {
        showApiError(err, this.props.intl);
      });
  };

  hideButtonAdd = () => {
    let { productLimit, totalProduct } = this.state;

    // Fix: infinity converts to null in JSON, so we check using Number.isFinite
    if (Number.isFinite(productLimit) && totalProduct >= productLimit) {
      this.setState({
        hide_button_add: true,
      });
    } else {
      this.setState({
        hide_button_add: false,
      });
    }
  };

  createProductSku = () => {
    const { v4: uuidv4 } = require("uuid");
    return `SP-${uuidv4().substring(0, 8).toUpperCase()}`;
  };

  renderSKU = () => {
    const sku = this.createProductSku();
    this.setState({
      sku,
    });
    return sku;
  };

  /** Axios func helper */
  dataListRender = () => {
    const { selectedPageSize, currentPage, search } = this.state.page;

    productApi.fetchProducts(selectedPageSize, currentPage, search)
      .then((data) => {
        const allCategories = this.dataListRenderCategories();
        const StockUnits = this.dataListRenderStocksUnit();
        const MakeProducts = this.dataListRenderMakeProduct();

        return Promise.all([allCategories, StockUnits, MakeProducts])
          .then((promises) => {
            let anyAccordion = [];
            let numeroPerguntas = data.data.length;
            for (var i = 0; i < numeroPerguntas; i++) {
              anyAccordion.push(false);
            }
            this.setState(
              (prev) => ({
                ...prev,
                data: data.data,
                data_dynamic_form: data.dataDynamicForm,
                data_categories: promises[0],
                data_stocks: promises[1],
                make_products: promises[2],
                is_loading_data: false,
                page: {
                  ...prev.page,
                  totalPage: data.totalPage,
                  totalItem: data.totalItem,
                },
                accordion: anyAccordion,
                productLimit: data.productLimit,
                totalProduct: data.totalProduct,
              }),
              () => {
                if (this.state.form.id) {
                  const {
                    id,
                    name,
                    barcode,
                    sku,
                    price,
                    image,
                    category,
                    category_id,
                    price_sale,
                    price_wholesale,
                    active_sale,
                    size,
                    brand_name,
                    sale_unit,
                    topping,
                    ice,
                    sugar,
                    sizeTitle,
                    toppingTitle,
                    iceTitle,
                    sugarTitle,
                    dynamic_form,
                    tax_rate,
                  } = this.state.form;
                  // const { count, stock, make } =
                  //     this.state.form_cost;

                  this.setState((prev) => ({
                    ...prev,
                    form: {
                      ...prev.form,
                      id,
                      name,
                      barcode,
                      sku,
                      price,
                      image,
                      category,
                      category_id,
                      price_sale,
                      price_wholesale,
                      active_sale,
                      size,
                      brand_name,
                      sale_unit,
                      topping,
                      ice,
                      sugar,
                      sizeTitle,
                      toppingTitle,
                      iceTitle,
                      sugarTitle,
                      dynamic_form,
                      tax_rate,
                      isTemplate: prev.form.isTemplate,
                    },
                    // form_cost: {
                    //     ...prev.form_cost,
                    //     count,
                    //     stock,
                    //     make,
                    // },
                  }));
                } else {
                  this.setState((prev) => ({
                    ...prev,
                    selectedItems: [],
                    show: {
                      ...prev.show,
                      update: false,
                      update_cost: false,
                    },
                    form: createDefaultProductForm(prev.business_type),
                    form_cost: DEFAULT_FORM_COST,
                  }));
                }
                // if(this.state.form_cost.id){
                //     const {
                //         id,
                //         count,
                //         stock,
                //         product,
                //         make,
                //         data_products,
                //         data_stocks,
                //         product_id
                //     } = this.state.form_cost

                //     this.setState((prev) => ({
                //         ...prev,
                //         form_cost: {
                //             ...prev.form_cost,
                //             id,
                //             count,
                //             stock,
                //             product,
                //             make,
                //             data_products,
                //             data_stocks,
                //             product_id
                //         },
                //     }));
                // } else {
                //     this.setState((prev) => ({
                //         ...prev,
                //         form_cost: DEFAULT_FORM_COST,
                //     }));
                // }
              },
            );
            this.hideButtonAdd();
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => {
        showApiError(err, this.props.intl);
        return false;
      });
  };

  dataListRenderCategories = () => productApi.fetchCategories();

  dataListRenderStocksUnit = () => productApi.fetchStockUnits();

  dataListRenderMakeProduct = () => productApi.fetchMakeProducts();

  dataRenderProviders = () => {
    productApi.fetchProviders()
      .then((providers) => {
        this.setState((prev) => ({ ...prev, data_providers: providers }));
      })
      .catch(() => {});
  };

  dataAllRender = () => {
    productApi.fetchAllProducts()
      .then((products) => {
        const allAttributes = products
          .flatMap((product) => product.variant || [])
          .map((v) => v.attributes || {});

        this.setState((prev) => ({
          ...prev,
          data_all: products,
          data_length: products.length,
          allSizeOptions: variantHandlers.extractAllUniqueOptions(allAttributes, "size"),
          allColorOptions: variantHandlers.extractAllUniqueOptions(allAttributes, "color"),
          allMaterialOptions: variantHandlers.extractAllUniqueOptions(allAttributes, "material"),
          pageMulti: {
            ...prev.pageMulti,
            totalItems: products.length,
          },
        }));
      })
      .catch((err) => {
        showApiError(err, this.props.intl);
      });
  };

  handleChangePageMulti = (page) => {
    this.setState((prev) => ({
      ...prev,
      pageMulti: {
        ...prev.pageMulti,
        currentPage: page,
      },
    }));
  };

  dataAllRenderSoftDel = () => {
    productApi.fetchSoftDeletedProducts().catch((err) => {
      showApiError(err, this.props.intl);
    });
  };

  dataAllRenderStockSoftDel = () => {
    productApi.fetchSoftDeletedStocks()
      .then((data) => {
        const getSKU = data.data.length + 1;
        this.setState((prev) => ({ ...prev, sku_stock: `NVL-${getSKU}` }));
      })
      .catch((err) => {
        showApiError(err, this.props.intl);
      });
  };

  onHandlerDelete = () => {
    if (!isD(ROLE_ALIAS)) return false;

    let tokenStr = localStorage.getItem("access_token");

    if (
      window.confirm(
        this.props.intl.formatMessage({ id: "product.confirm.delete" }),
      )
    ) {
      axios
        .delete(`${END_POINT + "/product"}`, {
          data: {
            arrayId: this.state.selectedItems,
          },
          headers: { Authorization: `Bearer ${tokenStr}` },
        })
        .then((res) => res.data)
        .then((data) => {
          NotificatioSucController(
            this.props.intl.formatMessage({ id: "home.notify.success" }),
          );

          this.socket.emit("create logs", {
            user_id: localStorage.getItem("user_id"),
            message: LogsHelper["product-delete"],
          });

          this.dataListRender();
          this.dataAllRender();
        })
        .catch((err) => {
          showApiError(err, this.props.intl);
          return false;
        });
    }
  };

  submitProductForm = async (save) => {
    if (this.productSubmissionPending) return false;
    this.productSubmissionPending = true;
    this.setState({ isSavingProduct: true });
    try {
      return await save();
    } catch (err) {
      showApiError(err, this.props.intl);
      return false;
    } finally {
      this.productSubmissionPending = false;
      this.setState({ isSavingProduct: false });
    }
  };

  resolveProductCategory = async () => {
    const { category, category_id } = this.state.form;
    if (!category?.__isNew__) return category?.id || category?.value || category_id;
    const response = await productApi.createCategory(
      category.label || category.value,
      this.state.form.dynamic_form,
    );
    const createdCategory = response.data.data;
    const option = { ...createdCategory, label: createdCategory.name, value: createdCategory.id };
    this.setState((prev) => ({
      form: { ...prev.form, category: option, category_id: createdCategory.id },
      data_categories: [...prev.data_categories, option],
    }));
    this.socket.emit("create logs", {
      user_id: localStorage.getItem("user_id"),
      message: LogsHelper["category-create"],
    });
    return createdCategory.id;
  };

  onHandlerUpdate = (e) => {
    if (!isU(ROLE_ALIAS)) return false;
    e?.preventDefault();
    return this.submitProductForm(this.saveProductUpdate);
  };

  saveProductUpdate = async () => {
    const {
      generatedVariants,
      // isLoadingSubmit,
      form: {
        id,
        name,
        sku,
        barcode,
        brand_name,
        sale_unit,
        softHide,
        weight,
        length,
        width,
        height,
        price,
        category_id,
        image,
        price_sale,
        price_wholesale,
        active_sale,
        size,
        color,
        material,
        topping,
        flavor,
        ice,
        sugar,
        fnb_type,
        sizeTitle,
        toppingTitle,
        iceTitle,
        sugarTitle,
        dynamic_form,
        tax_rate,
        stock_quantity,
        stock_cost_price,
        ingredients,
        isTemplate,
      },
    } = this.state;

    if (
      !id ||
      !name ||
      (isMissingPrice(price) && !hasSizeBasedPrice(size, this.state.business_type)) ||
      !category_id
    ) {
      NotificatioErrController(
        this.props.intl.formatMessage({ id: "product.error.invalid-data" }),
      );
      return;
    }

    // Kiểm tra giá bán của các biến thể (nếu có)
    const hasInvalidVariant = generatedVariants.some((variant) => {
      return variant.price == null || variant.price === "";
    });

    if (hasInvalidVariant) {
      NotificatioErrController(
        this.props.intl.formatMessage({ id: "product.error.variant-required" }),
      );
      return; // Dừng toàn bộ hàm, không chạy tiếp
    }

    const resolvedCategoryId = await this.resolveProductCategory();

    const resolvedSku = String(sku || "").trim() || this.createProductSku();
    if (!String(sku || "").trim()) {
      this.setState((prev) => ({
        sku: resolvedSku,
        form: {
          ...prev.form,
          sku: resolvedSku,
        },
      }));
    }

    // 1. Xử lý mảng biến thể để gửi lên server
    const variantsToSend = variantHandlers.allocateVariantSkus(generatedVariants, resolvedSku).map((variant) => {
      // Định dạng lại ngày tháng và loại bỏ dấu phẩy ở giá tiền
      return {
        id: Number.isInteger(variant.id) ? variant.id : null,
        sku: variant.sku,
        price: CleanPrice(variant.price),
        price_wholesale: CleanPrice(variant.price_wholesale),
        promo_price: CleanPrice(variant.promo_price),
        ...variantHandlers.getVariantInventoryPayload(variant, this.state.inventorySnapshot?.variants),
        exchange_value: CleanPrice(variant.exchange_value || 1),
        date_start: variant.date_start
          ? moment(variant.date_start).format("YYYY-MM-DD")
          : null,
        date_end:
          variant.date_end && variant.date_end > variant.date_start
            ? moment(variant.date_end).format("YYYY-MM-DD")
            : null,
        attributes: {
          // Đóng gói size và color vào attributes
          size: variant.size,
          color: variant.color,
          material: variant.material,
        },
      };
    });

    let formData = new FormData();
    const normalizedBarcode = String(barcode ?? "").trim();
    // Handle multiple new images
    if (this.state.form.images && this.state.form.images.length > 0) {
      this.state.form.images.forEach((file) => {
        formData.append("images", file);
      });
    }

    // Handle kept images (images_old)
    if (Array.isArray(this.state.form.images_old)) {
      formData.append("images_old", JSON.stringify(this.state.form.images_old));
    }

    if (image) {
      formData.append("image", image);
    }

    formData.append("id", id);
    formData.append("name", name);
    if (normalizedBarcode) {
      formData.append("barcode", normalizedBarcode);
    }
    formData.append("sku", resolvedSku);
    formData.append("brand_name", brand_name || "");
    formData.append("sale_unit", sale_unit || "");
    formData.append("softHide", softHide ? "true" : "false");
    formData.append("weight", weight || "");
    formData.append("length", length || "");
    formData.append("width", width || "");
    formData.append("height", height || "");
    formData.append(
      "price",
      getSubmittedProductPrice(price, size, this.state.business_type),
    );
    formData.append("price_sale", CleanPrice(price_sale));
    formData.append("price_wholesale", CleanPrice(price_wholesale));
    formData.append("active_sale", active_sale);
    formData.append(
      "tax_rate",
      tax_rate !== undefined && tax_rate !== null && tax_rate !== ""
        ? tax_rate
        : 0,
    );
    formData.append("category_id", resolvedCategoryId);
    const filteredSize = getPersistedSizes(
      size,
      this.state.business_type,
    );

    formData.append("size", JSON.stringify(filteredSize));
    formData.append("topping", JSON.stringify(getPersistedToppings(topping)));
    formData.append("flavor", JSON.stringify(getPersistedFlavors(flavor)));
    const submittedFnbType =
      fnb_type || getDefaultFnbType(this.state.business_type);
    const fnbOptions = normalizeFnbPayloadOptions({
      fnbType: submittedFnbType,
      ice,
      sugar,
    });
    if (isFoodServiceBusiness(this.state.business_type)) {
      formData.append("fnb_type", submittedFnbType);
    }
    formData.append("ice", JSON.stringify(fnbOptions.ice));
    formData.append("sugar", JSON.stringify(fnbOptions.sugar));
    formData.append("sizeTitle", sizeTitle);
    formData.append("toppingTitle", toppingTitle);
    formData.append("iceTitle", iceTitle);
    formData.append("sugarTitle", sugarTitle);
    formData.append("dynamic_form", JSON.stringify(dynamic_form));

    const optionSizes = getPersistedSizes(size, this.state.business_type);
    formData.append(
      "sizeOptions",
      JSON.stringify(
        this.state.business_type === "cafe" ||
          this.state.business_type === "restaurant"
          ? optionSizes
          : size || [],
      ),
    );
    formData.append("colorOptions", JSON.stringify(color || []));
    formData.append("materialOptions", JSON.stringify(material || []));

    // Gửi mảng các biến thể đã được xử lý
    formData.append("variants", JSON.stringify(variantsToSend));

    if (
      this.state.business_type &&
      this.state.business_type !== "cafe" &&
      this.state.business_type !== "restaurant" &&
      !generatedVariants.length
    ) {
      if (variantHandlers.hasInventoryValue(stock_quantity)) {
        formData.append("stock_quantity", CleanPrice(stock_quantity));
      }
      if (variantHandlers.hasInventoryValue(stock_cost_price)) {
        formData.append("stock_cost_price", CleanPrice(stock_cost_price));
      }
    }

    // Gửi định lượng nguyên liệu (cho Cafe/Restaurant)
    if (isFoodServiceBusiness(this.state.business_type)) {
      formData.append("ingredients", JSON.stringify(ingredients || []));
    }

    let tokenStr = localStorage.getItem("access_token");
    const updateRequest = isTemplate
      ? axios.post(`${END_POINT}/product/${id}/convert-template`, formData, {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        })
      : axios.put(`${END_POINT + "/product"}`, formData, {
        headers: {
          Authorization: `Bearer ${tokenStr}`,
        },
      });

    await updateRequest
      .then((res) => res.data)
      .then(async (data) => {
        // console.log('dataWhenPutProduct: ', data.data);
        NotificatioSucController("Thành công");

        this.socket.emit("create logs", {
          user_id: localStorage.getItem("user_id"),
          message: LogsHelper["product-update"],
        });

        this.dataListRender();
        this.dataAllRender();

        // if (
        //     this.state.form_cost?.make?.length &&
        //     this.state.form_cost?.make_new?.length
        // ) {
        //     await this.onHandlerAddStock();
        //     await this.onAddCost(data.data);
        // } else if (this.state.form_cost?.make_new?.length) {
        //     await this.onHandlerAddStock();
        // } else if (this.state.form_cost?.make?.length) {
        //     await this.onAddCost(data.data);
        // }

        // if (
        //     !show_quantitative &&
        //     Number(price_cost) > 0 &&
        //     Number(quantity) > 0
        // ) {
        //     await this.onHandlerAddStock();
        // }

        this.setState((prev) => ({
          ...prev,
          show: {
            ...prev.show,
            add: false,
            update: false,
            update_cost: false,
          },
          // isLoadingSubmit: true,
          editProduct: false,
          generatedVariants: [], // Reset biến thể về mặc định
          form: createDefaultProductForm(prev.business_type),
          form_cost: DEFAULT_FORM_COST,
        }));
      })
      .catch((err) => {
        showApiError(err, this.props.intl);
        return false;
      });
  };
  // onHandlerUpdateCost = () => {
  //     if (!isU(ROLE_ALIAS)) return false;
  //     this.toggle('update_cost');
  // };

  lookupBarcodeFromTemplate = async (barcode) => {
    const sequence = this.barcodeLookupSequence;
    try {
      const response = await axios.get(`${END_POINT}/product-template/lookup/${encodeURIComponent(barcode)}`);
      if (sequence !== this.barcodeLookupSequence ||
          String(this.state.form.barcode ?? "").trim() !== barcode) return;
      if (response.data && response.data.found) {
        const item = response.data.data;
        NotificatioSucController(`Tìm thấy sản phẩm mẫu: ${item.name}`);

        // Find matching category ID in current category list
        const matchedCategory = this.state.data_categories.find(
          (c) => (c.label || c.value || "").toLowerCase() === (item.category_name || "").toLowerCase()
        );

        this.setState((prevState) => {
          const sugPrice = item.suggested_price && parseFloat(item.suggested_price) > 0
            ? String(Math.round(parseFloat(item.suggested_price)))
            : (prevState.form.price || "");
          const sugCost = item.suggested_cost_price && parseFloat(item.suggested_cost_price) > 0
            ? String(Math.round(parseFloat(item.suggested_cost_price)))
            : (prevState.form.stock_cost_price || prevState.form.price_cost || "");
          const imageUrl = item.image
            ? (item.image.startsWith("http") || item.image.startsWith("data:")
              ? item.image
              : `${END_POINT}${item.image}`)
            : "";

          return {
            form: {
              ...prevState.form,
              name: item.name,
              brand_name: item.brand_name || "",
              sale_unit: item.sale_unit || "Cái",
              image: imageUrl,
              images_old: imageUrl ? [imageUrl] : [],
              category_id: matchedCategory ? matchedCategory.id : "",
              category: matchedCategory ? matchedCategory : (item.category_name ? { label: item.category_name, value: item.category_name, __isNew__: true } : ""),
              barcode: item.barcode,
              price: sugPrice,
              price_cost: sugCost,
              stock_cost_price: sugCost,
            }
          };
        });
      }
    } catch (err) {
      console.error("Error looking up barcode from template:", err);
    }
  };

  handleKeyPress = async (e) => {
    const sequence = this.barcodeLookupSequence = (this.barcodeLookupSequence || 0) + 1;
    const barcodeValue = String(this.state.form.barcode ?? "").trim();
    if (e.key === "Enter") e.preventDefault();
    if (barcodeValue.length < 3) return;
    const matches = (product) => String(product.barcode ?? "").trim() === barcodeValue;
    let product = this.state.data_all.find(matches) || this.state.data.find(matches);
    if (!product && e.key === "Enter") {
      try {
        const products = await productApi.fetchAllProducts();
        if (sequence !== this.barcodeLookupSequence ||
            String(this.state.form.barcode ?? "").trim() !== barcodeValue) return;
        product = products.find(matches);
        this.setState({ data_all: products });
      } catch (err) {
        showApiError(err, this.props.intl);
        return;
      }
    }
    if (product) {
      if (this.state.editProduct && this.state.form.id === product.id) return;
      return this.onClickedUpdate(product);
    }
    if (e.key === "Enter") return this.lookupBarcodeFromTemplate(barcodeValue);
  };

  validateCreateProductForm = () => {
    const {
      business_type,
      generatedVariants,
      form: { name, price, size },
    } = this.state;

    if (!name) {
      NotificatioErrController(
        this.props.intl.formatMessage({ id: "product.error.name-required" }),
      );
      return false;
    }

    if (isMissingPrice(price) && !hasSizeBasedPrice(size, business_type)) {
      NotificatioErrController(
        this.props.intl.formatMessage({ id: "product.error.price-required" }),
      );
      return false;
    }

    const hasInvalidVariant = generatedVariants.some((variant) => {
      return (
        variant.price == null ||
        variant.price === ""
      );
    });

    if (hasInvalidVariant) {
      NotificatioErrController(
        this.props.intl.formatMessage({
          id: "product.error.variant-required",
        }),
      );
      return false;
    }

    return true;
  };

  buildCreateProductFormData = (categoryId) => {
    const {
      generatedVariants,
      business_type,
      form: {
        name,
        barcode,
        brand_name,
        sale_unit,
        softHide,
        weight,
        length,
        width,
        height,
        price,
        image,
        price_sale,
        price_wholesale,
        active_sale,
        size,
        color,
        material,
        topping,
        flavor,
        ice,
        sugar,
        fnb_type,
        sizeTitle,
        toppingTitle,
        iceTitle,
        sugarTitle,
        dynamic_form,
        tax_rate,
        stock_quantity,
        stock_cost_price,
        ingredients,
      },
    } = this.state;

    const formData = new FormData();

    if (this.state.form.images && this.state.form.images.length > 0) {
      this.state.form.images.forEach((file) => {
        formData.append("images", file);
      });
    }

    if (image) {
      formData.append("image", image);
    }

    const productSku = this.state.sku || this.createProductSku();
    if (!this.state.sku) {
      this.setState({ sku: productSku });
    }

    const persistedSizes = getPersistedSizes(size, business_type);
    const persistedToppings = getPersistedToppings(topping);
    const persistedFlavors = getPersistedFlavors(flavor);
    const submittedFnbType = fnb_type || getDefaultFnbType(business_type);
    const fnbOptions = normalizeFnbPayloadOptions({
      fnbType: submittedFnbType,
      ice,
      sugar,
    });
    const variantsToSend = variantHandlers.allocateVariantSkus(generatedVariants, productSku).map((variant) => {
      return {
        sku: variant.sku,
        price: CleanPrice(variant.price),
        price_wholesale: CleanPrice(variant.price_wholesale),
        promo_price: CleanPrice(variant.promo_price),
        quantity: getOptionalInventoryValue(variant.quantity),
        cost_price: getOptionalInventoryValue(variant.cost_price),
        exchange_value: CleanPrice(variant.exchange_value || 1),
        date_start: variant.date_start
          ? moment(variant.date_start).format("YYYY-MM-DD")
          : null,
        date_end:
          variant.date_end && variant.date_end > variant.date_start
            ? moment(variant.date_end).format("YYYY-MM-DD")
            : null,
        attributes: {
          size: variant.size,
          color: variant.color,
          material: variant.material,
        },
      };
    });

    formData.append("name", name);
    if (barcode) formData.append("barcode", barcode);
    formData.append("sku", productSku);
    formData.append("brand_name", brand_name || "");
    formData.append("sale_unit", sale_unit || "");
    formData.append("softHide", softHide ? "true" : "false");
    formData.append("weight", weight || "");
    formData.append("length", length || "");
    formData.append("width", width || "");
    formData.append("height", height || "");
    formData.append(
      "price",
      getSubmittedProductPrice(price, size, business_type),
    );
    formData.append("price_sale", CleanPrice(price_sale));
    formData.append("price_wholesale", CleanPrice(price_wholesale));
    formData.append("active_sale", active_sale);
    formData.append(
      "tax_rate",
      tax_rate !== undefined && tax_rate !== null && tax_rate !== ""
        ? tax_rate
        : 0,
    );
    if (categoryId) formData.append("category_id", categoryId);
    formData.append("size", JSON.stringify(persistedSizes));
    formData.append("topping", JSON.stringify(persistedToppings));
    formData.append("flavor", JSON.stringify(persistedFlavors));
    if (isFoodServiceBusiness(business_type)) {
      formData.append("fnb_type", submittedFnbType);
    }
    formData.append("ice", JSON.stringify(fnbOptions.ice));
    formData.append("sugar", JSON.stringify(fnbOptions.sugar));
    formData.append("sizeTitle", sizeTitle);
    formData.append("toppingTitle", toppingTitle);
    formData.append("iceTitle", iceTitle);
    formData.append("sugarTitle", sugarTitle);
    formData.append("dynamic_form", JSON.stringify(dynamic_form));
    formData.append(
      "sizeOptions",
      JSON.stringify(
        business_type === "cafe" || business_type === "restaurant"
          ? persistedSizes
          : size || [],
      ),
    );
    formData.append("colorOptions", JSON.stringify(color || []));
    formData.append("materialOptions", JSON.stringify(material || []));
    formData.append("variants", JSON.stringify(variantsToSend));

    if (
      business_type &&
      business_type !== "cafe" &&
      business_type !== "restaurant" &&
      !generatedVariants.length
    ) {
      if (variantHandlers.hasInventoryValue(stock_quantity)) {
        formData.append("stock_quantity", CleanPrice(stock_quantity));
      }
      if (variantHandlers.hasInventoryValue(stock_cost_price)) {
        formData.append("stock_cost_price", CleanPrice(stock_cost_price));
      }
    }

    if (
      (business_type === "cafe" || business_type === "restaurant") &&
      ingredients &&
      ingredients.length > 0
    ) {
      formData.append("ingredients", JSON.stringify(ingredients));
    }

    return formData;
  };

  resetCreateProductForm = (newProductId) => {
    const nextSku = this.createProductSku();
    this.setState((prev) => ({
      ...prev,
      form: createDefaultProductForm(prev.business_type),
      form_cost: {
        ...DEFAULT_FORM_COST,
        new_product_id: newProductId,
      },
      generatedVariants: [],
      sku: nextSku,
    }));
  };

  handleCreateProductSuccess = (product) => {
    NotificatioSucController(
      this.props.intl.formatMessage({ id: "home.notify.success" }),
    );

    this.socket.emit("create logs", {
      user_id: localStorage.getItem("user_id"),
      message: LogsHelper["product-create"],
    });

    this.resetCreateProductForm(product?.id);
    this.dataListRender();
    this.dataAllRender();
    this.dataAllRenderSoftDel();
    this.dataAllRenderStockSoftDel();
  };

  onHandlerAdd = async (e) => {
    if (!isC(ROLE_ALIAS)) return false;
    e?.preventDefault();

    return this.submitProductForm(async () => {
      if (!this.validateCreateProductForm()) return;

      if (this.state.form.category?.__isNew__) {
        return await this.onHandlerAddCategory();
      }

      try {
        const formData = this.buildCreateProductFormData(
          this.state.form.category?.id || this.state.form.category?.value,
        );

        // --- Gửi request ---
        let tokenStr = localStorage.getItem("access_token");
        const productResponse = await axios.post(
          `${END_POINT}/product`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${tokenStr}`,
            },
          },
        );

        this.handleCreateProductSuccess(productResponse.data.data);
      } catch (err) {
        showApiError(err, this.props.intl);
      }
    });
  };

  onHandlerAddCategory = async () => {
    const id = await this.resolveProductCategory();
    return this.onHandlerAddProductAfterCategory(id);
  };

  onHandlerAddProductAfterCategory = async (id) => {
    // if (!isC(ROLE_ALIAS)) return false;
    // e.preventDefault();

    if (!this.validateCreateProductForm()) return;

    let tokenStr = localStorage.getItem("access_token");
    try {
      const productResponse = await axios.post(
        `${END_POINT + "/product"}`,
        this.buildCreateProductFormData(id),
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        },
      );

      this.handleCreateProductSuccess(productResponse.data.data);
    } catch (err) {
      showApiError(err, this.props.intl);
      return false;
    }
  };

  onHandlerSoftHide = (id) => {
    if (!isU(ROLE_ALIAS)) return false;

    if (!id) return false;

    let tokenStr = localStorage.getItem("access_token");
    axios
      .post(
        `${END_POINT + "/product/softhide"}`,
        {
          id: id,
        },
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        },
      )
      .then((res) => {
        return res.data;
      })
      .then((data) => {
        NotificatioSucController(data.responseText);

        this.dataListRender();
        this.dataAllRender();
      })
      .catch((err) => {
        showApiError(err, this.props.intl);
      });
  };

  onHandlerBestter = (id) => {
    if (!isU(ROLE_ALIAS)) return false;

    if (!id) return false;

    let tokenStr = localStorage.getItem("access_token");
    axios
      .post(
        `${END_POINT + "/product/bestter"}`,
        {
          id: id,
        },
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        },
      )
      .then((res) => {
        return res.data;
      })
      .then((data) => {
        NotificatioSucController(data.responseText);

        this.dataListRender();
        this.dataAllRender();
        this.setState((prev) => ({
          ...prev,
          form: {
            ...prev.form,
            form: DEFAULT_FORM,
          },
        }));
      })
      .catch((err) => {
        showApiError(err, this.props.intl);
      });
  };

  // Bulk handler cho nhiều sản phẩm được chọn - Ẩn/Hiện
  onHandlerBulkSoftHide = async () => {
    if (!isU(ROLE_ALIAS)) return false;

    const { selectedItems } = this.state;
    if (!selectedItems.length) {
      NotificatioErrController("Vui lòng chọn sản phẩm để thao tác");
      return;
    }

    let tokenStr = localStorage.getItem("access_token");

    try {
      for (const id of selectedItems) {
        await axios.post(
          `${END_POINT + "/product/softhide"}`,
          { id },
          { headers: { Authorization: `Bearer ${tokenStr}` } },
        );
      }

      NotificatioSucController(`Đã cập nhật ${selectedItems.length} sản phẩm`);
      this.dataListRender();
      this.dataAllRender();
      this.setState({ selectedItems: [] });
    } catch (err) {
      NotificatioErrController(
        err.response?.data?.responseText || "Có lỗi xảy ra",
      );
    }
  };

  // Bulk handler cho nhiều sản phẩm được chọn - Khuyên dùng
  onHandlerBulkBestter = async () => {
    if (!isU(ROLE_ALIAS)) return false;

    const { selectedItems } = this.state;
    if (!selectedItems.length) {
      NotificatioErrController("Vui lòng chọn sản phẩm để thao tác");
      return;
    }

    let tokenStr = localStorage.getItem("access_token");

    try {
      for (const id of selectedItems) {
        await axios.post(
          `${END_POINT + "/product/bestter"}`,
          { id },
          { headers: { Authorization: `Bearer ${tokenStr}` } },
        );
      }

      NotificatioSucController(`Đã cập nhật ${selectedItems.length} sản phẩm`);
      this.dataListRender();
      this.dataAllRender();
      this.setState({ selectedItems: [] });
    } catch (err) {
      NotificatioErrController(
        err.response?.data?.responseText || "Có lỗi xảy ra",
      );
    }
  };
  /** Axios func helper */

  /** State func helper */
  toggleAddFromTemplate = () => {
    this.setState((prev) => ({
      showAddFromTemplate: !prev.showAddFromTemplate,
    }));
  };

  toggle = (name) => {
    if (name === "add" && !isC(ROLE_ALIAS)) return false;
    if (name === "update" && !isU(ROLE_ALIAS)) return false;
    if (name === "update_cost" && !isU(ROLE_ALIAS)) return false;
    const nextSku =
      name === "add" || name === "" ? this.createProductSku() : null;
    this.editLoadSequence = (this.editLoadSequence || 0) + 1;
    this.barcodeLookupSequence = (this.barcodeLookupSequence || 0) + 1;

    this.setState((prev) => ({
      ...prev,
      show: {
        ...prev.show,
        update:
          name === "add" || name === "update_cost" ? false : prev.show.update,
        update_cost:
          name === "add" || name === "update" ? false : prev.show.update_cost,
        view: name === "add" ? false : prev.show.view,
        [name]: !prev.show[name],
      },
      selectedItems: name === "add" ? [] : prev.selectedItems,
      editProduct: name === "add" || name === "" ? false : prev.editProduct,
      inventorySnapshot: name === "add" || name === "" ? null : prev.inventorySnapshot,
      generatedVariants:
        name === "add" || name === "" ? [] : prev.generatedVariants,
      sku: nextSku || prev.sku,
      form:
        name === "add" || name === ""
          ? createDefaultProductForm(prev.business_type)
          : prev.form,
      // form_cost: name === 'add' || name === ''  ? DEFAULT_FORM_COST : prev.form_cost,
    }));
    if (name === "add") {
      this.dataListRender();
    }
  };

  hideDisplayContent = () => {
    this.setState((prev) => ({
      ...prev,
      show: {
        ...prev.show,
        view: false,
      },
    }));
  };

  onChangeSizeAndTopping = (value, name, id, type) => {
    this.setState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: prev.form[name].map((item) =>
          item.id === id ? { ...item, [type]: value } : item,
        ),
      },
    }));
  };

  onChangeIceAndSugar = ({ name, value }) => {
    // Đảm bảo currentArray luôn là một mảng
    const currentArray = Array.isArray(this.state.form[name])
      ? this.state.form[name]
      : [];

    let _filter_value;
    if (currentArray.includes(value)) {
      // Nếu đã có, loại bỏ giá trị
      _filter_value = currentArray.filter((_item) => _item !== value);
    } else {
      // Nếu chưa có, thêm vào (tạo mảng mới thay vì mutate)
      _filter_value = [...currentArray, value];
    }

    this.setState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: _filter_value,
      },
    }));
  };

  onAddSizeAndTopping = (name) => {
    let value_more = {
      id: RandomString(5),
      name: "",
      value: "0",
    };

    this.setState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: [...prev.form[name], value_more],
      },
    }));
  };

  onRemoveSizeAndTopping = (name, id) => {
    const remove_value = this.state.form[name].filter((_elm) => _elm.id !== id);
    this.setState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: remove_value,
      },
    }));
  };

  onChangeForm = (e, _name) => {
    // if (!e)
    //   return false;
    if (e && e.target) {
      let {
        target: { name, value },
      } = e;

      if (name === "image" || name === "images") {
        const imageFiles = Array.from(e.target.files || []).filter(
          (file) => file.type && file.type.startsWith("image/"),
        );

        this.setState((prev) => {
          const currentImages = prev.form.images || [];
          const existingImageCount = (prev.form.images_old || []).length;
          const availableSlots = Math.max(
            0,
            10 - existingImageCount - currentImages.length,
          );
          const selectedFiles = imageFiles
            .slice(0, availableSlots)
            .map((file) => {
              file.preview = URL.createObjectURL(file);
              return file;
            });

          return {
            ...prev,
            form: {
              ...prev.form,
              images: [...currentImages, ...selectedFiles],
            },
          };
        });
        e.target.value = "";
        return;
      }

      this.setState((prev) => {
        const nextFields = { [name]: value };
        if (name === "fnb_type" && value === FNB_TYPE_DRINK) {
          nextFields.ice = ensureDrinkOptionValues(prev.form.ice);
          nextFields.sugar = ensureDrinkOptionValues(prev.form.sugar);
        }

        return {
          ...prev,
          form: {
            ...prev.form,
            ...nextFields,
          },
        };
      });
    } else {
      let updatedFields = { [_name]: e };
      if (_name === "category" && e) {
        updatedFields.category_id = e.id || e.value;
      }
      this.setState((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          ...(_name === "fnb_type" && e === FNB_TYPE_DRINK
            ? {
                ice: ensureDrinkOptionValues(prev.form.ice),
                sugar: ensureDrinkOptionValues(prev.form.sugar),
              }
            : {}),
          ...updatedFields,
        },
      }));
    }
  };

  onChangeFormCost = (e, _name) => {
    if (e && e.target) {
      let {
        target: { name, value },
      } = e;

      this.setState((prev) => ({
        ...prev,
        form_cost: {
          ...prev.form_cost,
          [name]: value,
        },
      }));
    } else {
      this.setState((prev) => ({
        ...prev,
        form_cost: {
          ...prev.form_cost,
          [_name]: e,
        },
      }));
    }
  };

  onChangeDynamicForm = (e, _name) => {
    if (e.target) {
      let {
        target: { name, value },
      } = e;

      this.setState((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          dynamic_form: {
            ...prev.form.dynamic_form,
            [name]: value,
          },
        },
      }));
    } else {
      let value = e.value ? e.value : e;

      this.setState((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          dynamic_form: {
            ...prev.form.dynamic_form,
            [_name]: value,
          },
        },
      }));
    }
  };

  onDeleteImage = (index = -1, type = "single") => {
    if (type === "new" && index > -1) {
      // Remove from new images (files)
      const newImages = [...this.state.form.images];
      newImages.splice(index, 1);
      this.setState((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          images: newImages,
        },
      }));
    } else if (type === "old" && index > -1) {
      // Remove from old images (URLs)
      const newImagesOld = [...this.state.form.images_old];
      newImagesOld.splice(index, 1);
      this.setState((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          images_old: newImagesOld,
        },
      }));
    } else {
      // Legacy single image delete
      this.setState((prev) => ({
        ...prev,
        form: {
          ...prev.form,
          image: "",
        },
      }));
    }
  };

  onSubmitForm = (type) => {
    if (type === "update") {
      this.onHandlerUpdate();
    } else if (type === "add") {
      this.onHandlerAdd();
    } else if (type === "update_cost") {
      this.onHandlerAddCost();
    }
  };

  onChangePage = (page) => {
    this.setState(
      (prev) => ({
        ...prev,
        page: {
          ...prev.page,
          currentPage: page,
        },
      }),
      () => this.dataListRender(),
    );
  };

  onChangePageMulti = (page) => {
    this.setState(
      (prev) => ({
        ...prev,
        pageMulti: {
          ...prev.pageMulti,
          currentPage: page,
        },
      }),
      () => this.dataListRender(),
    );
  };

  performSearch = (searchTerm) => {
    // Chuẩn hóa từ khóa tìm kiếm trước khi sử dụng
    const normalizedSearchTerm = searchTerm;

    // Tùy chọn: Chỉ search nếu từ khóa chuẩn hóa thay đổi
    if (
      normalizedSearchTerm === this.state.page.search &&
      normalizedSearchTerm !== ""
    ) {
      // Không làm gì nếu từ khóa đã chuẩn hóa không đổi và không rỗng
    } else if (normalizedSearchTerm === "" && this.state.page.search === "") {
      // Không làm gì nếu cả hai đều rỗng
    } else {
      this.setState(
        (prev) => ({
          ...prev,
          page: {
            ...prev.page,
            search: normalizedSearchTerm, // Lưu từ khóa đã chuẩn hóa
            // currentPage: 1,
          },
        }),
        () => {
          this.dataListRender();
        },
      );
    }
  };

  handleInputChange = (e) => {
    const currentValue = e.target.value;
    // Cập nhật inputValue với giá trị gốc, chưa chuẩn hóa, để hiển thị đúng trên input
    this.setState({ inputValue: currentValue });

    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId);
    }

    this.searchTimeoutId = setTimeout(() => {
      // Khi thực hiện search, chúng ta sẽ chuẩn hóa currentValue
      this.performSearch(currentValue);
    }, 500);
  };

  handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
        this.searchTimeoutId = null;
      }
      // Khi thực hiện search, chúng ta sẽ chuẩn hóa giá trị hiện tại của input
      this.performSearch(this.state.inputValue);
    }
  };

  onChangePageSize = (size) => {
    this.setState(
      (prev) => ({
        ...prev,
        page: {
          ...prev.page,
          selectedPageSize: size,
          currentPage: 1,
        },
      }),
      () => this.dataListRender(),
    );
  };

  menuButtonClick = (e, menuClickCount, containerClassnames) => {
    setTimeout(() => {
      var event = document.createEvent("HTMLEvents");
      event.initEvent("resize", false, false);
      window.dispatchEvent(event);
    }, 350);
    this.props.setContainerClassnames(
      menuClickCount,
      containerClassnames,
      this.props.selectedMenuHasSubItems,
    );
  };

  onClickedItem = async (item, event) => {
    // Lấy giá trị cần thiết ra khỏi event NGAY LẬP TỨC
    const isShiftKeyDown = event.shiftKey;

    // Luôn sử dụng setState với callback để đảm bảo an toàn
    this.setState((prev) => {
      const { data, selectedItems, lastChecked, data_all } = prev;
      let newSelectedItems = [];

      // TRƯỜNG HỢP 1: Người dùng giữ phím Shift
      if (isShiftKeyDown && lastChecked !== null) {
        const startIndex = data.findIndex((i) => i.id === lastChecked);
        const endIndex = data.findIndex((i) => i.id === item.id);

        // Lấy ra các item nằm trong khoảng shift-click
        const rangeStart = Math.min(startIndex, endIndex);
        const rangeEnd = Math.max(startIndex, endIndex);

        if (rangeStart !== -1 && rangeEnd !== -1) {
          const itemsToSelect = data.slice(rangeStart, rangeEnd + 1);
          const idsToSelect = itemsToSelect.map((item) => item.id);

          // Dùng Set để gộp mảng item đã chọn và mảng mới, tự động loại bỏ trùng lặp
          // Đây là cách làm bất biến, tạo ra một Set mới rồi chuyển về mảng
          const combinedIds = new Set([...selectedItems, ...idsToSelect]);
          newSelectedItems = Array.from(combinedIds);
        } else {
          // Nếu không tìm thấy 1 trong 2 item, thì coi như click bình thường
          newSelectedItems = [...selectedItems, item.id];
        }
      }
      // TRƯỜNG HỢP 2: Click bình thường (không giữ Shift)
      else {
        const isSelected = selectedItems.includes(item.id);
        if (isSelected) {
          // Tạo mảng mới không chứa item đã click
          newSelectedItems = selectedItems.filter((id) => id !== item.id);
        } else {
          // Tạo mảng mới với item vừa click
          newSelectedItems = [...selectedItems, item.id];
        }
      }

      return {
        selectedItems: newSelectedItems,
        lastChecked: item.id, // Luôn cập nhật item cuối cùng được click
        // show: {
        //     ...prev.show,
        //     view: true,
        //     update: false,
        //     update_cost: false,
        // },
        // sku: item.sku,
        // form: {
        //     ...prev.form,
        //     id: item.id,
        //     name: item.name,
        //     sku: item.sku,
        //     barcode: item.barcode || '',
        //     price: parseInt(item.price, 10),
        //     price_sale: parseInt(item.price_sale, 10) || '',
        //     active_sale: item.active_sale || false,
        //     category: item.category,
        //     category_id: item.category_id,
        //     image: item.image || '',
        //     size: item.sizes,
        //     color: item.colors,
        //     material: item.materials,
        //     variant: item.variant,
        //     // topping,
        //     // ice,
        //     // sugar,
        //     // sizeTitle,
        //     // toppingTitle,
        //     // iceTitle,
        //     // sugarTitle,
        //     // dynamic_form,
        // },
        // selectedItems: [item.id],
        // Cập nhật danh sách các biến thể để component con render
        // generatedVariants: item.variant.map(v => ({
        //     ...v, isOpen: false,
        //     size: v.attributes.size || {},
        //     color: v.attributes.color || {},
        //     material: v.attributes.material || {},
        // })),
      };
    });
  };

  onClickedUpdate = async (item) => {
    if (!item?.id) return;
    this.barcodeLookupSequence = (this.barcodeLookupSequence || 0) + 1;
    const sequence = this.editLoadSequence = (this.editLoadSequence || 0) + 1;
    const formAtOpen = this.state.form;
    let fullProductData = this.state.data_all.find((product) => String(product.id) === String(item.id)) || item;
    const hasLoadedVariants = (product) =>
      [product.variant, product.variants, product.product_variants].some(Array.isArray);
    if (!hasLoadedVariants(fullProductData)) {
      try {
        const products = await productApi.fetchAllProducts();
        if (sequence !== this.editLoadSequence || this.state.form !== formAtOpen) return;
        fullProductData = products.find((product) => String(product.id) === String(item.id));
        if (!fullProductData || !hasLoadedVariants(fullProductData)) {
          NotificatioErrController(this.props.intl.formatMessage({ id: "product.notify.no-detail" }));
          return;
        }
        this.setState({ data_all: products });
      } catch (err) {
        showApiError(err, this.props.intl);
        return;
      }
    }
    const {
      sku,
      size,
      topping,
      ice,
      sugar,
      sizeTitle,
      toppingTitle,
      iceTitle,
      sugarTitle,
      dynamic_form,
    } = fullProductData;

    // Nếu không tìm thấy sản phẩm, dừng lại để tránh lỗi
    if (!fullProductData) {
      console.error(
        this.props.intl.formatMessage({ id: "product.notify.no-detail" }),
        item?.id,
      );
      NotificatioErrController(
        this.props.intl.formatMessage({ id: "product.notify.no-detail" }),
      );
      return;
    }

    // Lấy mảng biến thể từ dữ liệu đầy đủ
    const variants =
      fullProductData.variant ||
      fullProductData.variants ||
      fullProductData.product_variants ||
      [];
    const normalizedVariants = variants.map(variantHandlers.normalizeVariant);
    const productStock =
      getDirectProductStock(
        fullProductData.id,
        this.state.make_products,
        this.state.data_stocks,
      ) ||
      this.state.data_stocks.find((stock) => stock.sku === fullProductData.sku);
    const hasVariants = variants.length > 0;
    const productIngredients = isFoodServiceBusiness(this.state.business_type)
      ? this.state.make_products
          .filter((makeProduct) => Number(makeProduct.product_id) === Number(fullProductData.id))
          .map((makeProduct) => ({
            stock_id: makeProduct.stock_id || makeProduct.stock?.id,
            stock_name: makeProduct.stock?.name || "",
            quantity: makeProduct.count,
            unit: makeProduct.stock?.unit || "",
          }))
      : [];

    // Trích xuất tất cả các attributes từ tất cả các biến thể
    const allAttributes = normalizedVariants.map((v) => v.attributes);

    // Helper function để lấy các giá trị duy nhất từ attributes
    const getUniqueOptions = (attributeName) =>
      variantHandlers.extractAllUniqueOptions(allAttributes, attributeName);

    const sizes = getUniqueOptions("size");
    const colors = getUniqueOptions("color");
    const materials = getUniqueOptions("material");

    // Cập nhật state của form
    this.setState((prev) => ({
      ...prev,
      show: {
        ...prev.show,
        add: true,
        update: false,
        update_cost: false,
        view: false,
      },
      sku,
      editProduct: true,
      inventorySnapshot: Object.freeze({
        variants: variantHandlers.snapshotVariantInventory(normalizedVariants),
      }),
      form: {
        ...createDefaultProductForm(prev.business_type),
        id: fullProductData.id,
        isTemplate: Boolean(fullProductData.isTemplate),
        name: decodeStoredProductText(fullProductData.name),
        sku: fullProductData.sku || item.sku || sku || "",
        barcode: String(fullProductData.barcode ?? item.barcode ?? ""),
        brand_name: decodeStoredProductText(fullProductData.brand_name || item.brand_name),
        sale_unit: decodeStoredProductText(
          fullProductData.sale_unit ||
            fullProductData.unit ||
            item.sale_unit ||
            item.unit ||
            productStock?.unit ||
            "",
        ),
        tax_rate:
          fullProductData.tax_rate !== undefined &&
          fullProductData.tax_rate !== null
            ? fullProductData.tax_rate
            : 0,
        softHide: Boolean(fullProductData.softHide),
        weight: fullProductData.weight || "",
        length: fullProductData.length || "",
        width: fullProductData.width || "",
        height: fullProductData.height || "",
        price: parseInt(fullProductData.price, 10),
        price_sale: parseInt(fullProductData.price_sale, 10) || "",
        price_wholesale: parseInt(fullProductData.price_wholesale, 10) || "",
        active_sale: fullProductData.active_sale || false,
        category: fullProductData.category,
        category_id: fullProductData.category_id,
        stock_quantity: "",
        stock_cost_price: hasVariants
          ? ""
          : getStockCostPriceValue(productStock, fullProductData),
        price_cost: hasVariants
          ? ""
          : getStockCostPriceValue(productStock, fullProductData),
        quantitative: hasVariants
          ? ""
          : getStockQuantityValue(productStock, fullProductData),
        image: fullProductData.image || "",
        images: [], // Reset new images selection
        images_old: parseStoredProductImages(fullProductData),
        size: (() => {
          // 1. Try to get sizes from Variants (Fashion/Retail)
          let sizeArray = sizes && sizes.length > 0 ? [...sizes] : [];

          // 2. If no variant sizes, try to get from ProductSettings (FnB) - NEW LOGIC
          if (
            sizeArray.length === 0 &&
            fullProductData.sizes &&
            fullProductData.sizes.length > 0
          ) {
            sizeArray = [...fullProductData.sizes];
          }
          // 3. Fallback to 'size' from item (Legacy/Props)
          else if (
            sizeArray.length === 0 &&
            Array.isArray(size) &&
            size.length > 0
          ) {
            sizeArray = [...size];
          }

          if (
            this.state.business_type === "cafe" ||
            this.state.business_type === "restaurant"
          ) {
            sizeArray = ensureDefaultSizes(sizeArray);
          }
          return sizeArray;
        })(),
        color:
          colors && colors.length > 0 ? colors : fullProductData.colors || [],
        material:
          materials && materials.length > 0
            ? materials
            : fullProductData.materials || [],
        topping: (() => {
          const toppingArray =
            fullProductData.toppings && fullProductData.toppings.length > 0
              ? fullProductData.toppings
              : Array.isArray(topping)
                ? topping
                : [];

          return this.state.business_type === "cafe" ||
            this.state.business_type === "restaurant"
            ? ensureDefaultToppings(toppingArray)
            : toppingArray;
        })(),
        flavor:
          this.state.business_type === "cafe" ||
          this.state.business_type === "restaurant"
            ? ensureDefaultFlavors(
                fullProductData.flavors || fullProductData.flavor,
              )
            : fullProductData.flavors || fullProductData.flavor || [],
        fnb_type:
          fullProductData.fnb_type ||
          getDefaultFnbType(this.state.business_type),
        ice: ensureDrinkOptionValues(
          fullProductData.ice && fullProductData.ice.length > 0
            ? fullProductData.ice
            : Array.isArray(ice)
              ? ice
              : [],
        ),
        sugar: ensureDrinkOptionValues(
          fullProductData.sugar && fullProductData.sugar.length > 0
            ? fullProductData.sugar
            : Array.isArray(sugar)
              ? sugar
              : [],
        ),
        sizeTitle: sizeTitle || "",
        toppingTitle: toppingTitle || "Topping",
        iceTitle: iceTitle || "Lượng đá",
        sugarTitle: sugarTitle || "Độ ngọt",
        dynamic_form: dynamic_form || {},
        ingredients: productIngredients,
      },
      selectedItems: [item.id],
      // Cập nhật danh sách các biến thể để component con render
      generatedVariants: normalizedVariants.map((v) => ({
        ...v,
        isOpen: false,
        size: v.attributes.size || {},
        color: v.attributes.color || {},
        material: v.attributes.material || {},
        cost_price: v.cost_price ?? "",
        exchange_value: v.exchange_value || "1",
      })),
    }));
  };

  onClickedUpdateCost = (item) => {
    this.setState((prev) => ({
      ...prev,
      show: {
        ...prev.show,
        update: false,
        update_cost: true,
      },
    }));
  };

  onClickedDelete = (item) => {
    let selectedSet = SetItemsSelected(
      item.id,
      this.state.selectedItems,
      "require",
    );
    this.setState(
      (prev) => ({
        ...prev,
        selectedItems: selectedSet,
      }),
      () => this.onHandlerDelete(),
    );
  };

  onClickedSoftHide = (item) => {
    this.setState(
      (prev) => ({
        ...prev,
        selectedItems: [item.id],
      }),
      () => this.onHandlerSoftHide(item.id),
    );
  };

  onClickedBestter = (item) => {
    this.setState(
      (prev) => ({
        ...prev,
        selectedItems: [item.id],
      }),
      () => this.onHandlerBestter(item.id),
    );
  };

  // onClickedCheckbox = (id) => {
  //     let selectedItems = this.state.selectedItems; // Lấy tham chiếu, không phải tạo bản sao
  //     if (selectedItems.includes(id)) {
  //         selectedItems = selectedItems.filter(x => x !== id);
  //     } else {
  //         selectedItems.push(id); // Lỗi! Đang thay đổi trực tiếp mảng trong state
  //     }

  //     this.setState((prev) => ({
  //         ...prev,
  //         selectedItems,
  //     }));
  // };

  onClickedCheckbox = (id, event) => {
    // Lấy trạng thái Shift key từ event
    const isShiftKeyDown = event?.shiftKey || false;

    // Sử dụng callback của setState để đảm bảo state luôn là mới nhất
    this.setState((prev) => {
      const { selectedItems, lastChecked, data } = prev;
      let newSelectedItems;

      // TRƯỜNG HỢP 1: Người dùng giữ phím Shift - chọn nhiều
      if (isShiftKeyDown && lastChecked !== null) {
        const startIndex = data.findIndex((item) => item.id === lastChecked);
        const endIndex = data.findIndex((item) => item.id === id);

        if (startIndex !== -1 && endIndex !== -1) {
          const rangeStart = Math.min(startIndex, endIndex);
          const rangeEnd = Math.max(startIndex, endIndex);

          const itemsToSelect = data.slice(rangeStart, rangeEnd + 1);
          const idsToSelect = itemsToSelect.map((item) => item.id);

          // Gộp các item đã chọn với các item trong range
          const combinedIds = new Set([...selectedItems, ...idsToSelect]);
          newSelectedItems = Array.from(combinedIds);
        } else {
          newSelectedItems = [...selectedItems, id];
        }
      }
      // TRƯỜNG HỢP 2: Click bình thường (toggle)
      else {
        const isSelected = selectedItems.includes(id);
        if (isSelected) {
          newSelectedItems = selectedItems.filter((itemId) => itemId !== id);
        } else {
          newSelectedItems = [...selectedItems, id];
        }
      }

      return {
        selectedItems: newSelectedItems,
        lastChecked: id, // Luôn cập nhật item cuối cùng được click
      };
    });
  };

  toggleAccordion = (tab) => {
    const prevState = this.state.accordion;
    const state = prevState.map((x, index) => (tab === index ? !x : false));
    this.setState({
      accordion: state,
    });
  };

  toggleAccordionVariations = (tabIndex) => {
    this.setState((prevState) => {
      const newVariants = prevState.generatedVariants.map((variant, index) => {
        if (index === tabIndex) {
          // Nếu là tab được click, đảo ngược trạng thái isOpen
          return { ...variant, isOpen: !variant.isOpen };
        }
        // Với các tab khác, luôn đóng lại
        return { ...variant, isOpen: false };
      });

      return { generatedVariants: newVariants };
    });
  };

  toggleTab = (tab) => {
    if (this.state.activeTab !== tab) {
      this.setState({
        activeTab: tab,
      });
    }
  };

  filterData = (key, value) => {
    const { data_all } = this.state;
    if (key === "" && value === "") {
      this.setState((prev) => ({
        ...prev,
        data: data_all,
        filter: null,
      }));
    } else if (key === "name") {
      const filteredItems = data_all.filter(
        (item) => item.category.name === value,
      );
      this.setState((prev) => ({
        ...prev,
        data: filteredItems,
        filter: { column: key, value },
      }));
    } else if (key === "make_product" && value === "done") {
      const make_products = this.state.make_products;
      const filteredProduct = data_all.filter((item) =>
        make_products.map((make) => make.product_id).includes(item.id),
      );
      // console.log(filteredProduct);
      this.setState((prev) => ({
        ...prev,
        data: filteredProduct,
        filter: { column: key, value },
      }));
    } else if (key === "make_product" && value === "not_yet") {
      const make_products = this.state.make_products;
      const filteredProduct = data_all.filter(
        (item) =>
          make_products.map((make) => make.product_id).indexOf(item.id) === -1,
      );
      // console.log(filteredProduct);
      this.setState((prev) => ({
        ...prev,
        data: filteredProduct,
        filter: { column: key, value },
      }));
    } else {
      const filteredItems = data_all.filter((item) => item[key] === value);
      this.setState((prev) => ({
        ...prev,
        data: filteredItems,
        filter: { column: key, value },
      }));
    }
  };

  onClickedFormAddStock = () => {
    const result = validateAndBuildStock(this.state.form_cost);
    if (!result.valid) {
      NotificatioErrController(result.error);
      return false;
    }
    this.setState((prev) => ({
      ...prev,
      form_cost: { ...prev.form_cost, ...result.newFormCost },
    }));
  };

  onClickedFormAddNewStock = () => {
    const result = validateAndBuildNewStock(this.state.form_cost);
    if (!result.valid) {
      NotificatioErrController(result.error);
      return false;
    }

    this.setState((prev) => ({
      ...prev,
      form_cost: {
        ...prev.form_cost,
        ...result.newFormCost,
      },
    }));
  };

  onClickedFormDeleteStock = (id, name) => {
    // console.log('onClickedFormDeleteStock: ', id, name);
    let make;
    if (name === "name") {
      make = this.state.form_cost.make_new.filter((s) => s.name !== id);
      this.onChangeFormCost(make, "make_new");
    } else if (name === "id") {
      make = this.state.form_cost.make.filter((s) => s.id !== id);
      this.onChangeFormCost(make, "make");
    } else if (name === "slice_make_product") {
      const make_products = this.state.make_products.filter(
        (make) => make.stock.id !== id,
      );
      this.setState((prev) => ({
        ...prev,
        make_products: make_products,
      }));
    }
  };

  onHandlerAddStock = async () => {
    if (!isC(ROLE_ALIAS)) return false;
    // e.preventDefault();

    const { make_new } = this.state.form_cost;
    const { name, sku, price_cost, quantity } = this.state.form;

    const priceCostWithoutCommaAndDot = CleanPrice(price_cost);
    const quantityWithoutCommaAndDot = CleanPrice(quantity);

    const stockNews = buildStockEntriesFromProduct(this.state.form, make_new);

    let tokenStr = localStorage.getItem("access_token");
    if (stockNews.length) {
      return await axios
        .post(`${END_POINT + "/stock/create-stock-from-product"}`, stockNews, {
          headers: { Authorization: `Bearer ${tokenStr}` },
        })
        .then((res) => res.data)
        .then(async (data) => {
          // console.log('data: ', data);
          NotificatioSucController("Thêm nguyên vật liệu thành công");

          // console.log('newStock: ', data.data);
          // console.log('data_stocks: ', this.state.data_stocks);

          this.socket.emit("create logs", {
            user_id: localStorage.getItem("user_id"),
            message: LogsHelper["stock-create"],
          });

          this.socket.emit("refresh-stock", {
            user_id: localStorage.getItem("user_id"),
          });

          await this.onAddNewCost({ data });
          await this.dataListRender();
          await this.dataAllRender();
          // this.toggle('add');
        })
        .catch((err) => {
          showApiError(err, this.props.intl);
          return false;
        });
    } else if (
      Number(priceCostWithoutCommaAndDot) > 0 &&
      Number(quantityWithoutCommaAndDot) > 0
    ) {
      return axios
        .post(
          `${END_POINT + "/stock"}`,
          {
            name,
            sku: sku ? sku : this.state.sku,
            count: quantityWithoutCommaAndDot,
            unit_price: priceCostWithoutCommaAndDot.toString(),
          },
          {
            headers: { Authorization: `Bearer ${tokenStr}` },
          },
        )
        .then((res) => res.data)
        .then(async (data) => {
          NotificatioSucController("Thêm nguyên liệu thành công");

          this.socket.emit("create logs", {
            user_id: localStorage.getItem("user_id"),
            message: LogsHelper["stock-create"],
          });

          this.socket.emit("refresh-stock", {
            user_id: localStorage.getItem("user_id"),
          });

          const new_cost = {
            name,
            // count: quantityWithoutCommaAndDot,
            count: 1,
          };
          // console.log('beforeAddNewCost');
          await this.onAddNewCost({ data, new_cost });

          // this.dataListRender();
          // this.dataAllRender();
          // this.dataAllRenderSoftDel();
        })
        .catch((err) => {
          showApiError(err, this.props.intl);
          return false;
        });
    }
  };

  onAddNewCost = ({ data, new_cost }) => {
    if (!isC(ROLE_ALIAS)) return false;

    const {
      form_cost: { new_product_id, make_new },
    } = this.state;

    const updatedMake = buildCostDataFromResponse(data, new_cost, make_new);

    try {
      productApi.saveMakeProduct(new_product_id, updatedMake)
        // .then((res) => res.data)
        .then((data) => {
          NotificatioSucController("Thêm định lượng thành công");

          this.socket.emit("create logs", {
            user_id: localStorage.getItem("user_id"),
            message: LogsHelper["make-product-create"],
          });

          // this.dataListRender();
          // this.dataAllRender();
          // this.setState((prev) => ({
          //     ...prev,
          //     // form: {
          //     //     ...prev.form,
          //     //     product: null,
          //     // },
          //     form_cost: {
          //         ...prev.form_cost,
          //         make: [],
          //         new_product_id: '',
          //     },
          // }));
          // this.toggle('update_cost');
        });
    } catch (err) {
      // Ném lỗi ra ngoài để hàm cha (onHandlerAdd) có thể bắt và xử lý
      console.error("Failed to save product recipe:", err);
      throw err; // Rất quan trọng!
    }
  };

  onAddCost = (data) => {
    // console.log('onAddCost: ', data);
    if (!isC(ROLE_ALIAS)) return false;
    // e.preventDefault();

    const { dynamic_form } = this.state.form;
    const { make } = this.state.form_cost;

    if (make.length < 1) {
      NotificatioErrController("Chưa thêm nguyên liệu");
      return false;
    }

    return productApi.saveMakeProduct(data.id, make, dynamic_form)
      .then(() => {
        NotificatioSucController("Thêm định lượng thành công");

        this.socket.emit("create logs", {
          user_id: localStorage.getItem("user_id"),
          message: LogsHelper["make-product-create"],
        });

        this.dataListRender();
        this.dataAllRender();
      })
      .catch((err) => {
        showApiError(err, this.props.intl);
        return false;
      });
  };

  onHandlerAddCost = (e) => {
    if (!isC(ROLE_ALIAS)) return false;
    e.preventDefault();

    const { dynamic_form, id } = this.state.form;
    const { make } = this.state.form_cost;

    if (make.length < 1) {
      NotificatioErrController("Chưa thêm nguyên liệu");
      return false;
    }

    productApi.saveMakeProduct(id, make, dynamic_form)
      .then(() => {
        NotificatioSucController("Thêm định lượng thành công");

        this.socket.emit("create logs", {
          user_id: localStorage.getItem("user_id"),
          message: LogsHelper["make-product-create"],
        });

        this.dataListRender();
        this.dataAllRender();
        this.setState((prev) => ({
          ...prev,
          // form: {
          //     ...prev.form,
          //     product: null,
          // },
          form_cost: {
            ...prev.form_cost,
            make: [],
          },
        }));
        this.toggle("update_cost");
      })
      .catch((err) => {
        showApiError(err, this.props.intl);
        return false;
      });
  };

  changeDisplayMode = (mode) => {
    this.setState((prev) => ({
      ...prev,
      displayMode: mode,
      show: {
        ...prev.show,
        view: false,
      },
    }));
    return false;
  };

  onShowMoreItem = () => {
    const { show_add_more_topping } = this.state;
    this.setState({
      show_add_more_topping: !show_add_more_topping,
    });
  };

  onShowQuantitative = () => {
    const { show_quantitative } = this.state;
    this.setState({
      show_quantitative: !show_quantitative,
    });
  };

  onClickedCheckbox = (id) => {
    this.setState((prev) => {
      const { selectedItems } = prev;
      const isSelected = selectedItems.includes(id);
      return {
        selectedItems: isSelected
          ? selectedItems.filter((item) => item !== id)
          : [...selectedItems, id],
      };
    });
  };

  handleChangeSelectAll = (isToggle) => {
    const { selectedItems, data, data_all, displayMode } = this.state;
    const targetData = displayMode === "list" ? data_all : data;

    if (selectedItems.length >= targetData.length) {
      if (isToggle) {
        this.setState({
          selectedItems: [],
        });
      }
    } else {
      this.setState({
        selectedItems: targetData.map((x) => x.id),
        selectedCategoryItems: [], // Clear category selection
      });
    }
    document.activeElement.blur();
    return false;
  };

  handleModernSelectAll = (ids = []) => {
    if (!ids.length) return false;

    this.setState((prev) => {
      const isAllSelected = ids.every((id) => prev.selectedItems.includes(id));
      return {
        selectedItems: isAllSelected ? [] : ids,
        selectedCategoryItems: [],
      };
    });
    if (document.activeElement) document.activeElement.blur();
    return false;
  };

  handleChangeSelectAllCategory = (isToggle) => {
    if (
      this.state.selectedCategoryItems.length >=
      this.state.data_categories.length
    ) {
      if (isToggle) {
        this.setState({
          selectedCategoryItems: [],
        });
      }
    } else {
      this.setState({
        selectedCategoryItems: this.state.data_categories.map((x) => x.id),
        selectedItems: [], // Clear product selection
      });
    }
    document.activeElement.blur();
    return false;
  };

  handleChangeSelectCategory = (id) => {
    if (this.state.selectedCategoryItems.includes(id)) {
      this.setState({
        selectedCategoryItems: this.state.selectedCategoryItems.filter(
          (x) => x !== id,
        ),
      });
    } else {
      this.setState({
        selectedCategoryItems: [...this.state.selectedCategoryItems, id],
        selectedItems: [], // Clear product selection
      });
    }
  };

  onHandlerDeleteCategory = () => {
    // Implement delete logic here or trigger a method in Category component
    // Since Category component has the delete logic, better to pass a trigger or move logic here.
    // For now, let's assume we pass a ref or context, BUT simpler:
    // Move onHandlerDelete logic from Category.js to here OR expose it?
    // User wants "logic xóa danh mục tại dropdown pageheader".
    // I can emit an event or call a method on the Category ref.
    // Let's use a ref for Category component.
    if (this.categoryRef) {
      this.categoryRef.onHandlerDelete();
    }
  };

  handleDataUpdate = (changedItems) => {
    console.log("Các hàng đã thay đổi được ghi nhận:", changedItems);

    this.setState((prevState) => {
      // Tạo một bản sao của object chứa các thay đổi
      const newChangedProducts = { ...prevState.changedProducts };

      // Cập nhật các thay đổi mới vào object
      changedItems.forEach((item) => {
        // Dùng `item.id` (hoặc sku) làm key.
        // Thao tác này sẽ thêm mới hoặc ghi đè nếu đã có thay đổi trước đó.
        if (item.id) {
          newChangedProducts[item.id] = item;
        }
      });

      // **CẬP NHẬT GIAO DIỆN (QUAN TRỌNG)**
      // Để người dùng thấy thay đổi ngay lập tức, chúng ta cũng cần cập nhật
      // dữ liệu đang hiển thị (`data_page`) và dữ liệu tổng (`data_all`).

      const updatedAllData = [...prevState.data_all];
      // const updatedPageData = [...prevState.data_page];

      changedItems.forEach((changedItem) => {
        // Cập nhật trong data_all
        const indexInAll = updatedAllData.findIndex(
          (p) => p.id === changedItem.id,
        );
        if (indexInAll !== -1) {
          updatedAllData[indexInAll] = changedItem;
        }

        // // Cập nhật trong data_page
        // const indexInPage = updatedPageData.findIndex(p => p.id === changedItem.id);
        // if (indexInPage !== -1) {
        //     updatedPageData[indexInPage] = changedItem;
        // }
      });

      return {
        changedProducts: newChangedProducts,
        data_all: updatedAllData,
        // data_page: updatedPageData,
      };
    });
  };

  handleImportProduct = (dataImport) => {
    console.log("Importing products:", dataImport);
    console.log("Role Check:", isC(ROLE_ALIAS), ROLE_ALIAS);
    if (!isC(ROLE_ALIAS)) return false;

    if (!dataImport || dataImport.length === 0) {
      NotificatioErrController("Không có dữ liệu để thêm");
      return;
    }

    const { business_type } = this.state;
    const tokenStr = localStorage.getItem("access_token");

    if (!tokenStr) {
      NotificatioErrController(
        "Lỗi: Chưa đăng nhập hoặc không tìm thấy token.",
      );
      return;
    }

    if (!END_POINT) {
      NotificatioErrController("Lỗi: Cấu hình END_POINT bị thiếu.");
      return;
    }

    console.log("Making request to:", `${END_POINT}/product/import-product`);

    return axios
      .post(
        `${END_POINT}/product/import-product`,
        {
          data: dataImport,
          business_type: business_type,
        },
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      )
      .then((res) => {
        console.log("Raw Response:", res);
        const { data } = res;
        // Backend returns: { success: true, data: { successful: [], failed: [] } }
        // Ensure we handle both potential structures (flat or nested) to be safe
        const successfulImports =
          data.successfulImports || data.data?.successful || [];
        const failedImports = data.failedImports || data.data?.failed || [];

        // Nếu thành công hết
        if (failedImports.length === 0) {
          NotificatioSucController(
            `Đã thêm thành công ${successfulImports.length} sản phẩm.`,
          );
        } else {
          NotificatioErrController(
            `Thêm thất bại ${failedImports.length} sản phẩm. Vui lòng kiểm tra lại.`,
          );
        }

        this.setState({
          successfulImports: successfulImports,
          failedImports: failedImports,
        });

        // Reload data
        this.dataListRender();
        this.dataAllRender();

        // Đóng modal thêm mới sau khi xử lý xong
        this.toggle("add");

        return { successfulImports, failedImports };
      })
      .catch((err) => {
        console.error("Import Error Full:", err);
        console.error("Response Data:", err.response?.data);
        console.error("Response Status:", err.response?.status);
        NotificatioErrController(
          err.response?.data?.responseText ||
            "Có lỗi xảy ra khi thêm sản phẩm: " + err.message,
        );
        throw err;
      });
  };

  handleSaveChanges = async () => {
    const { changedProducts } = this.state;

    // Chuyển object các sản phẩm đã thay đổi thành một mảng
    const dataToSend = Object.values(changedProducts);

    if (dataToSend.length === 0) {
      console.log("Không có thay đổi nào để lưu.");
      // Có thể hiển thị thông báo cho người dùng
      return;
    }

    console.log("Gửi NHỮNG HÀNG THAY ĐỔI NÀY lên server:", dataToSend);

    try {
      const tokenStr = localStorage.getItem("access_token");

      // Gọi đến endpoint mới cho cập nhật hàng loạt
      const response = await axios.put(
        `${END_POINT}/product/bulk-update`, // <== ENDPOINT ĐÃ THAY ĐỔI
        dataToSend,
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        },
      );

      // Xử lý kết quả thành công từ backend
      const data = response.data; // axios bọc response trong .data
      NotificatioSucController(data.responseText || "Cập nhật thành công!");

      this.socket.emit("create logs", {
        user_id: localStorage.getItem("user_id"),
        message: LogsHelper["product-update"],
      });

      // Quan trọng: Sau khi lưu thành công, reset lại state `changedProducts`
      this.setState({ changedProducts: {} });

      // Tải lại dữ liệu nếu cần
      this.dataListRender();
      this.dataAllRender();
    } catch (err) {
      // Xử lý lỗi từ backend
      const errorMessage =
        err.response?.data?.responseText || "Có lỗi xảy ra, vui lòng thử lại.";
      NotificatioErrController(errorMessage);
    }
  };

  onSaveRecipe = async (item) => {
    const tokenStr = localStorage.getItem("access_token");
    if (!tokenStr) {
      alert("Lỗi: Chưa đăng nhập hoặc không tìm thấy token.");
      return;
    }

    axios
      .post(
        `${END_POINT}/product/update-recipes`,
        { recipes: item },
        {
          headers: {
            Authorization: `Bearer ${tokenStr}`,
          },
        },
      )
      .then((res) => {
        NotificatioSucController(
          res.data.message || "Cập nhật định lượng thành công!",
        );
        this.dataListRender();
        this.dataAllRender();
        // alert(res.data.message || 'Cập nhật định lượng thành công!');
        // Có thể reload lại dữ liệu tại đây
      })
      .catch((err) => {
        console.error("Lỗi khi cập nhật định lượng:", err);
        const errorMsg =
          err.response?.data?.message ||
          "Có lỗi xảy ra, không thể gửi yêu cầu.";
        alert(errorMsg);
      });
  };

  // handleSaveOCR = (tableData) => {
  //     try {
  //         const tokenStr = localStorage.getItem('access_token');

  //         // Gọi đến endpoint mới mà chúng ta sẽ tạo ở backend
  //         const response = axios.post(
  //             `${END_POINT}/product/bulk-upsert-from-image`,
  //             tableData, // Gửi toàn bộ mảng dữ liệu
  //             {
  //                 headers: {
  //                     Authorization: `Bearer ${tokenStr}`,
  //                 },
  //             }
  //         );

  //         NotificatioSucController(response.data.responseText || 'Import sản phẩm thành công!');
  //         this.dataListRender();
  //         this.dataAllRender();

  //     } catch (err) {
  //         const errorMessage = err.response?.data?.responseText || 'Có lỗi xảy ra, vui lòng thử lại.';
  //         NotificatioErrController(errorMessage);
  //     }
  // }

  // handleGenerateVariants = () => {
  //     // Bước 1: Hiển thị confirm() để xác nhận
  //     const isConfirmed = window.confirm(
  //         "Bạn có chắc chắn muốn tạo tự động tất cả các biến thể? Các biến thể đã tạo trước đó sẽ bị ghi đè."
  //     );

  //     // Nếu người dùng không đồng ý, dừng hàm tại đây
  //     if (!isConfirmed) {
  //         return;
  //     }

  //     const { size, color, material } = this.state.form;

  //     // Kiểm tra xem đã có size và color chưa
  //     if (!size || size.length === 0) {
  //         alert("Vui lòng chọn ít nhất một Size trước khi tạo.");
  //         return;
  //     }

  //     // BƯỚC 1: TẠO MỘT MẢNG "MÀU HIỆU QUẢ"
  //     // Nếu mảng color có dữ liệu, dùng nó.
  //     // Nếu không, tạo một mảng chứa một object rỗng.
  //     const effectiveColors = (color && color.length > 0)
  //         ? color
  //         : [{ label: '', value: 'no-color' }]; // 'no-color' để tránh trùng ID

  //     // MỚI: Tạo mảng hiệu quả cho material
  //     const effectiveMaterials = (material && material.length > 0)
  //         ? material
  //         : [{ label: '', value: 'no-material' }];

  //     // BƯỚC 2: SỬ DỤNG CÙNG MỘT LOGIC LẶP CHO CẢ 2 TRƯỜNG HỢP
  //     // Dùng flatMap cho code ngắn gọn hơn
  //     const allVariants = size.flatMap(s =>
  //         effectiveColors.flatMap(c =>
  //             effectiveMaterials.map(m => ({
  //                 id: `${s.value}-${c.value}-${m.value}`,
  //                 size: s,
  //                 color: c,
  //                 material: m,
  //                 price: '0',
  //                 promo_price: '0',
  //                 quantity: '',
  //                 date_start: null,
  //                 date_end: null,
  //                 isOpen: false
  //             }))
  //         )
  //     );

  //     // BƯỚC 3: CẬP NHẬT STATE
  //     this.setState({
  //         generatedVariants: allVariants
  //     });
  // };

  handleGenerateVariants = () => {
    const { generatedVariants } = this.state;
    const { price, price_wholesale, stock_cost_price, size, color, material } =
      this.state.form;

    const result = variantHandlers.generateVariants({
      existingVariants: generatedVariants,
      price,
      priceWholesale: price_wholesale,
      costPrice: stock_cost_price,
      size,
      color,
      material,
    });

    if (result === null) {
      alert(this.props.intl.formatMessage({ id: "product.error.select-size" }));
      return;
    }

    this.setState({ generatedVariants: variantHandlers.allocateVariantSkus(result, this.state.form.sku || this.state.sku) });
  };

  handleVariantChange = (value, variantId, fieldName) => {
    this.setState((prevState) => ({
      generatedVariants: variantHandlers.updateVariantField(
        prevState.generatedVariants, variantId, fieldName, value,
      ),
    }));
  };

  handleMasterSettingsChange = (value, fieldName) => {
    this.setState((prevState) => ({
      masterVariantSettings: {
        ...prevState.masterVariantSettings,
        [fieldName]: value,
      },
    }));
  };

  applyMasterSettingsToAll = () => {
    const isConfirmed = window.confirm(
      this.props.intl.formatMessage({ id: "product.confirm.apply-master" }),
    );
    if (!isConfirmed) return;

    this.setState((prevState) => ({
      generatedVariants: variantHandlers.applyMasterToAll(
        prevState.generatedVariants, prevState.masterVariantSettings,
      ),
    }));
  };

  handleDeleteVariant = (variantIdToDelete) => {
    const isConfirmed = window.confirm(
      this.props.intl.formatMessage({ id: "product.confirm.delete" }),
    );
    if (isConfirmed) {
      this.setState((prevState) => ({
        generatedVariants: variantHandlers.removeVariant(
          prevState.generatedVariants, variantIdToDelete,
        ),
      }));
    }
  };

  render() {
    const { match } = this.props;
    const {
      business_type,
      page,
      pageMulti,
      hide_button_add,
      selectedItems,
      data,
      data_all,
      filter,
      data_categories,
      data_stocks,
      make_products,
      changedProducts,
      is_loading_data,
      show,
      form,
      sku,
      form_cost,
      accordion,
      displayMode,
      show_add_more_topping,
      show_quantitative,
      editProduct,
      successfulImports,
      failedImports,
      generatedVariants,
      masterVariantSettings,
      allSizeOptions,
      allColorOptions,
      allMaterialOptions,
      // Dynamic Form
      data_dynamic_form,
      selectedCategoryItems,
      data_providers,
    } = this.state;
    const { intl } = this.props;
    const { messages } = intl;
    const buildProductFieldOptions = (field) => {
      const uniqueValues = new Map();

      (data_all || []).forEach((product) => {
        const rawValue =
          product?.[field] ?? product?.dataValues?.[field] ?? "";
        const value = String(rawValue).trim();
        const normalizedValue = value.toLocaleLowerCase("vi");

        if (value && !uniqueValues.has(normalizedValue)) {
          uniqueValues.set(normalizedValue, value);
        }
      });

      return Array.from(uniqueValues.values())
        .sort((first, second) => first.localeCompare(second, "vi"))
        .map((value) => ({ label: value, value }));
    };
    const brandOptions = buildProductFieldOptions("brand_name");
    const saleUnitOptions = buildProductFieldOptions("sale_unit");
    // console.log('business_type: ', business_type)
    // console.log("generatedVariants: ", generatedVariants);

    // Chuyển object các sản phẩm đã thay đổi thành một mảng
    const dataToSend = Object.values(changedProducts);

    if (!isR(ROLE_ALIAS))
      return (
        <Card className="text-muted mh-40vh h-100">
          <CardBody className="d-flex justify-content-center align-items-center">
            <IntlMessages id="home.notify.no-permission" />
          </CardBody>
        </Card>
      );

    // Tính toán dữ liệu cho trang hiện tại
    const firstPageIndex = (pageMulti.currentPage - 1) * PAGE_SIZE_MULTI;
    const lastPageIndex = firstPageIndex + PAGE_SIZE_MULTI;
    const currentTableDataMulti = data_all.slice(firstPageIndex, lastPageIndex);
    // Tính tổng số trang
    const totalPageMulti = Math.ceil(pageMulti.totalItems / PAGE_SIZE_MULTI);

    return is_loading_data ? (
      <div className="loading" />
    ) : (
      <div
        // className={classnames(
        //     'disable-text-selection',
        //     displayMode === 'thumblist' ? 'app-row' : ''
        // )}
        className={classnames("disable-text-selection", {
          "product-modern-page": USE_MODERN_PRODUCT_UI,
          "app-row": !USE_MODERN_PRODUCT_UI,
        })}
      >
        {/* VMASS_PRODUCT_UI_ROLLBACK_HEADING_START: legacy heading preserved for rollback. */}
        {!USE_MODERN_PRODUCT_UI ? (
          <PageHeading
            heading="menu.thumb-list"
            hide_button_add={hide_button_add}
            onAddFromTemplate={this.state.hasTemplates ? this.toggleAddFromTemplate : null}
            hideSearch={true}
            setting={page}
            match={match}
            data={data}
            data_all={data_all}
            totalItem={page.totalItem}
            isDisabledAdd={!isC(ROLE_ALIAS)}
            toggleFunc={() => {
              this.toggle("add");
              this.toggleAccordion();
            }}
            changePageSizeFunc={this.onChangePageSize}
            // onSearchKeyFunc={this.onSearchKey}
            displayMode={displayMode}
            changeDisplayMode={this.changeDisplayMode}
            handleImportProduct={(data) => {
              this.handleImportProduct(data);
            }}
            failedImports={failedImports}
            successfulImports={successfulImports}
            handleInputChange={this.handleInputChange}
            handleKeyDown={this.handleKeyDown}
            handleChangeSelectAll={
              this.state.activeTab === "2"
                ? this.handleChangeSelectAllCategory
                : this.handleChangeSelectAll
            }
            selectedItemsLength={
              this.state.activeTab === "2"
                ? selectedCategoryItems.length
                : selectedItems
                  ? selectedItems.length
                  : 0
            }
            itemsLength={
              this.state.activeTab === "2"
                ? data_categories
                  ? data_categories.length
                  : 0
                : data
                  ? data.length
                  : 0
            }
            onHandlerDelete={
              this.state.activeTab === "2"
                ? this.onHandlerDeleteCategory
                : this.onHandlerDelete
            }
            selectedItems={
              this.state.activeTab === "2" ? selectedCategoryItems : selectedItems
            }
            showExport={this.state.activeTab !== "2"}
            showAddNew={this.state.activeTab !== "2"}
            categories={data_categories}
            data_stocks={data_stocks}
            make_products={make_products}
            onSaveRecipe={this.onSaveRecipe}
            onBulkSoftHide={this.onHandlerBulkSoftHide}
            onBulkBestter={this.onHandlerBulkBestter}
            onHandlerEditProduct={this.onClickedUpdate}
            onAddCategory={() => {
              this.setState((prev) => ({
                showAddCategory: !prev.showAddCategory,
              }));
            }}
            // handleSaveOCR={this.handleSaveOCR}
          />
        ) : null}
        {/* VMASS_PRODUCT_UI_ROLLBACK_HEADING_END */}
        <AddProduct
            business_type={business_type}
            title={
              editProduct ? (
                <IntlMessages id="product.title.update" />
              ) : (
                <IntlMessages id="product.title" />
              )
            }
            isOpen={show.add}
            show_add_more_topping={show_add_more_topping}
            onShowMoreItem={this.onShowMoreItem}
            show_quantitative={show_quantitative}
            onShowQuantitative={this.onShowQuantitative}
            toggle={() => {
              this.toggle("add");
            }}
            onAddSizeAndTopping={this.onAddSizeAndTopping}
            onChangeSizeAndTopping={this.onChangeSizeAndTopping}
            onRemoveSizeAndTopping={this.onRemoveSizeAndTopping}
            data_dynamic_form={data_dynamic_form}
            values={form}
            form_cost={form_cost}
            sku={sku}
            data_categories={data_categories}
            data_stocks={data_stocks}
            make_products={make_products}
            setting={show}
            editProduct={editProduct}
            isSavingProduct={this.state.isSavingProduct}
            onChange={this.onChangeForm}
            onChangeCost={this.onChangeFormCost}
            onAddStock={this.onClickedFormAddStock}
            onAddNewStock={this.onClickedFormAddNewStock}
            onDeleteImage={this.onDeleteImage}
            onDeleteStock={this.onClickedFormDeleteStock}
            onChangeIceAndSugar={this.onChangeIceAndSugar}
            onChangeDynamicForm={this.onChangeDynamicForm}
            onSubmit={this.onHandlerAdd}
            onUpdate={this.onHandlerUpdate}
            handleKeyPress={this.handleKeyPress}
            generatedVariants={generatedVariants}
            handleGenerateVariants={this.handleGenerateVariants}
            toggleAccordionVariations={this.toggleAccordionVariations}
            handleVariantChange={this.handleVariantChange}
            masterVariantSettings={masterVariantSettings}
            handleMasterSettingsChange={this.handleMasterSettingsChange}
            applyMasterSettingsToAll={this.applyMasterSettingsToAll}
            allSizeOptions={allSizeOptions}
            allColorOptions={allColorOptions}
            allMaterialOptions={allMaterialOptions}
            brandOptions={brandOptions}
            saleUnitOptions={saleUnitOptions}
            handleDeleteVariant={this.handleDeleteVariant}
          />
        <AddCategoryProduct
          title={<IntlMessages id="make-product.add-new-modal-title" />}
          onSubmit={this.onHandlerAddCost}
          isOpen={show.update_cost}
          toggle={() => {
            this.toggle("update_cost");
          }}
          data_dynamic_form={data_dynamic_form}
          data_categories={data_categories}
          data_stocks={data_stocks}
          make_products={make_products}
          values={form}
          form_cost={form_cost}
          setting={show}
          onChangeCost={this.onChangeFormCost}
          onDeleteImage={this.onDeleteImage}
          onAddStock={this.onClickedFormAddStock}
          onDeleteStock={this.onClickedFormDeleteStock}
          onChangeDynamicForm={this.onChangeDynamicForm}
        />
        <AddFromTemplateModal
          isOpen={this.state.showAddFromTemplate}
          toggle={this.toggleAddFromTemplate}
          businessType={this.state.business_type}
          onImportSuccess={() => {
            this.dataListRender();
            this.dataAllRender();
          }}
        />
        {USE_MODERN_PRODUCT_UI ? (
          this.state.activeTab === "2" ? (
            <div className="product-modern-category-manager">
              <div className="product-modern-category-header">
                <div>
                  <h2>Quản lý danh mục</h2>
                  <p>Thêm, sửa và sắp xếp nhóm sản phẩm đang sử dụng.</p>
                </div>
                <div className="product-modern-category-header-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() =>
                      this.setState((prev) => ({
                        showAddCategory: !prev.showAddCategory,
                      }))
                    }
                  >
                    <i className="simple-icon-plus mr-2" />
                    Tạo danh mục
                  </button>
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => this.toggleTab("1")}
                  >
                    <i className="simple-icon-arrow-left mr-2" />
                    Quay lại sản phẩm
                  </button>
                </div>
              </div>
              <Category
                match={match}
                selectedItems={selectedCategoryItems}
                onCheckChange={this.handleChangeSelectCategory}
                externalToggleAdd={this.state.showAddCategory}
              />
            </div>
          ) : (
            <ProductModernDashboard
              data_all={data_all}
              data_categories={data_categories}
              data_providers={data_providers}
              data_stocks={data_stocks}
              make_products={make_products}
              business_type={business_type}
              showTemplate={this.state.showTemplate}
              onAddFromTemplate={this.state.hasTemplates ? this.toggleAddFromTemplate : null}
              page={page}
              selectedItems={selectedItems}
              onRefresh={() => {
                this.dataListRender();
                this.dataAllRender();
              }}
              hide_button_add={hide_button_add}
              canCreate={isC(ROLE_ALIAS)}
              canUpdate={isU(ROLE_ALIAS)}
              canDelete={isD(ROLE_ALIAS)}
              onAddProduct={() => {
                this.toggle("add");
                this.toggleAccordion();
              }}
              onImportProduct={(data) => {
                this.handleImportProduct(data);
              }}
              onSelectAllProducts={this.handleModernSelectAll}
              onToggleProduct={this.onClickedCheckbox}
              onUpdateProduct={this.onClickedUpdate}
              onUpdateCost={this.onClickedUpdateCost}
              onDeleteProduct={this.onClickedDelete}
              onSoftHideProduct={this.onClickedSoftHide}
              onBestterProduct={this.onClickedBestter}
              onBulkDelete={this.onHandlerDelete}
              onBulkSoftHide={this.onHandlerBulkSoftHide}
              onBulkBestter={this.onHandlerBulkBestter}
              onOpenCategories={() => this.toggleTab("2")}
              onAddCategory={() => {
                this.setState({ activeTab: "2" }, () => {
                  this.setState((prev) => ({
                    showAddCategory: !prev.showAddCategory,
                  }));
                });
              }}
              onSearch={this.performSearch}
            />
          )
        ) : (
          /* VMASS_PRODUCT_UI_ROLLBACK_BODY_START: legacy product list/category tabs preserved for rollback. */
          <>
        {Array.isArray(data_all) && data_all.some((p) => Boolean(p.isTemplate)) ? (
          <Row className="mb-3">
            <Colxx xxs="12">
              <TemplateWarningCard
                onDismissed={() => {
                  this.setState({ showTemplate: false });
                  this.dataListRender();
                  this.dataAllRender();
                  this.fetchUserTemplateVisibility();
                }}
              />
            </Colxx>
          </Row>
        ) : null}
        <Nav
          tabs
          className="d-flex justify-content-between separator-tabs ml-0 mb-5"
        >
          <div className="d-flex">
            <NavItem>
              <NavLink
                className={classnames({
                  active: this.state.activeTab === "1",
                  "nav-link": true,
                })}
                onClick={() => this.toggleTab("1")}
                location={{}}
                to="#"
              >
                <IntlMessages id="general.info" />
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({
                  active: this.state.activeTab === "2",
                  "nav-link": true,
                })}
                onClick={() => this.toggleTab("2")}
                location={{}}
                to="#"
              >
                <IntlMessages id="category.nav-tab" />
                <TooltipItem item={this.state.categoriesTooltip} id={16} />
              </NavLink>
            </NavItem>
          </div>

          {/* <NavItem>
                        <NavLink
                            className={classnames({
                                active: this.state.activeTab === '3',
                                'nav-link': true,
                            })}
                            onClick={() => this.toggleTab('3')}
                            location={{}}
                            to='#'
                        >
                            <span className='mr-2'>
                                <i className='simple-icon-settings'></i>
                            </span>
                            <IntlMessages id='general.setting' />
                        </NavLink>
                    </NavItem> */}
        </Nav>
        <TabContent activeTab={this.state.activeTab} className="mh-53vh">
          <TabPane tabId="1">
            {data.length === 0 ? (
              <Card className="mt-4 mb-4 mh-53vh">
                <CardBody>
                  <IntlMessages id="home.summary.no-data" />
                </CardBody>
              </Card>
            ) : (
              <>
                <Row className="content-page__row d-none d-md-flex">
                  <Col
                    xs="12"
                    sm="12"
                    md="12"
                    lg={show.view && displayMode === "thumblist" ? 7 : 12}
                    xl={show.view && displayMode === "thumblist" ? 7 : 12}
                  >
                    {data?.length ? (
                      displayMode === "thumblist" ? (
                        <>
                          {data?.map((item, idx) => (
                            <ListItemLeftForm
                              // onClickedItem={this.onClickedItem}
                              onUpdate={this.onClickedUpdate}
                              onUpdateCost={this.onClickedUpdateCost}
                              onDelete={this.onClickedDelete}
                              onSoftHide={this.onClickedSoftHide}
                              onBestter={this.onClickedBestter}
                              onChangeCheckbox={this.onClickedCheckbox}
                              isDisabledUpdate={!isU(ROLE_ALIAS)}
                              isDisabledUpdateCost={!isU(ROLE_ALIAS)}
                              isDisabledDelete={!isD(ROLE_ALIAS)}
                              selectedItems={selectedItems}
                              key={item.id}
                              idx={idx}
                              item={item}
                              make_products={make_products}
                              business_type={business_type}
                            />
                          ))}
                          {/* {data?.length <
                                                    12 ? null : ( */}
                          <Pagination
                            currentPage={page.currentPage}
                            totalPage={page.totalPage}
                            onChangePage={(i) => this.onChangePage(i)}
                          />
                          {/* )} */}
                        </>
                      ) : (
                        <div>
                          <ContentMultiForm
                            data={currentTableDataMulti}
                            categories={data_categories}
                            make_products={make_products}
                            data_stocks={data_stocks}
                            onUpdateData={this.handleDataUpdate}
                            page={pageMulti.currentPage}
                            pageSize={PAGE_SIZE_MULTI}
                            selectedItems={selectedItems}
                            toggleItem={this.onClickedCheckbox}
                            onSelectAll={this.handleChangeSelectAll}
                            business_type={business_type}
                            onDelete={this.onClickedDelete}
                          />
                          <Row className="align-items-center">
                            <Colxx
                              sm="7"
                              className="d-flex justify-content-center"
                            >
                              <Pagination
                                currentPage={pageMulti.currentPage}
                                totalPage={totalPageMulti}
                                onChangePage={(i) => this.onChangePageMulti(i)}
                              />
                            </Colxx>
                            <Colxx
                              sm="5"
                              className="d-flex justify-content-center mt-4 mb-4"
                            >
                              <button
                                onClick={this.handleSaveChanges}
                                className="btn btn-primary"
                                disabled={!dataToSend.length ? true : false}
                              >
                                <IntlMessages id="home.btn.save-all" />
                              </button>
                            </Colxx>
                          </Row>
                        </div>
                      )
                    ) : (
                      <Card className="d-flex justify-content-center align-items-center">
                        <CardBody>Chưa có dữ liệu...</CardBody>
                      </Card>
                    )}
                  </Col>
                  {show.view ? (
                    <Col xs="12" sm="12" md="12" lg="5" xl="5">
                      <ContentDisplay
                        // data_dynamic_form={data_dynamic_form}
                        values={form}
                        sku={sku}
                        setting={show}
                        onChange={this.onChangeForm}
                      />
                      <ContentDisplayCost
                        toggleFunc={() => {
                          this.toggle("update_cost");
                        }}
                        values={form}
                        make_products={make_products}
                        // form_cost={form_cost}
                        // setting={show}
                        data_stocks={data_stocks}
                      />
                    </Col>
                  ) : null}
                </Row>
                <Row className="content-page__row d-block d-md-none mb-4">
                  <Col>
                    {data?.length ? (
                      data?.map((item, idx) => {
                        // console.log(item);
                        return (
                          <div className="d-flex mb-3 card" key={item.id}>
                            <ListItemFormMobile
                              onClickedItem={this.onClickedItem}
                              onUpdate={this.onClickedUpdate}
                              onUpdateCost={this.onClickedUpdateCost}
                              onDelete={this.onClickedDelete}
                              onSoftHide={this.onClickedSoftHide}
                              onBestter={this.onClickedBestter}
                              onChangeCheckbox={this.onClickedCheckbox}
                              isDisabledUpdate={!isU(ROLE_ALIAS)}
                              isDisabledUpdateCost={!isU(ROLE_ALIAS)}
                              isDisabledDelete={!isD(ROLE_ALIAS)}
                              selectedItems={selectedItems}
                              idx={idx}
                              item={item}
                              accordion={accordion}
                              toggleAccordion={this.toggleAccordion}
                              onChange={this.onChangeForm}
                              make_products={make_products}
                              data_stocks={data_stocks}
                              sku={sku}
                              business_type={business_type}
                            />
                          </div>
                        );
                      })
                    ) : (
                      <Card className="d-flex justify-content-center align-items-center">
                        <CardBody>Chưa có dữ liệu...</CardBody>
                      </Card>
                    )}
                    <Pagination
                      currentPage={page.currentPage}
                      totalPage={page.totalPage}
                      onChangePage={(i) => this.onChangePage(i)}
                    />
                  </Col>
                </Row>
              </>
            )}
          </TabPane>
          <TabPane tabId="2">
            <Row>
              <Colxx>
                {this.state.activeTab === "2" ? (
                  <Category
                    match={match}
                    selectedItems={selectedCategoryItems}
                    onCheckChange={this.handleChangeSelectCategory}
                    externalToggleAdd={this.state.showAddCategory}
                  />
                ) : null}
              </Colxx>
            </Row>
          </TabPane>
          {/* <TabPane tabId='3'>
                        <Setting />
                    </TabPane> */}
        </TabContent>

        <ProductApplicationMenu
          data={data_all}
          make_products={make_products}
          filter={filter}
          filterData={(key, value) => this.filterData(key, value)}
        />
        <Separator />
        <FooterPage>
          <FooterProduct />
        </FooterPage>
          </>
          /* VMASS_PRODUCT_UI_ROLLBACK_BODY_END */
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ menu, searchInput }) => {
  const { containerClassnames, menuClickCount, selectedMenuHasSubItems } = menu;
  const { keyword: searchKeyword } = searchInput;
  return {
    containerClassnames,
    menuClickCount,
    selectedMenuHasSubItems,
    searchKeyword,
  };
};
export default injectIntl(
  connect(mapStateToProps, {
    setContainerClassnames,
    clickOnMobileMenu,
    setSearchContext,
  })(ContentComponent),
);
